import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  useNavigate,
} from 'react-router-dom'
import {
  useProvideFeedbackContext,
} from '../components/feedback/FeedbackContext'
import {
  CheckCircle2,
  Target,
  ChevronRight,
  Play,
  RotateCcw,
  X,
  ListTodo,
  MessageSquare,
  Send,
  Loader2,
} from 'lucide-react'
import {
  Button,
} from '../components/ui/Button'
import {
  useToast,
} from '../components/ui/Toast'
import {
  profileService,
} from '../services/profileService'
import {
  connectionForCompletedItem,
} from '../services/hortonGuidanceService'

import {
  addDays,
  startOfDay,
} from 'date-fns'
import {
  useServerTime,
} from '../hooks/useServerTime'

import {
  useCoachAlerts,
} from '../hooks/useCoachAlerts'
import {
  useHabitAdjustmentCheckin,
} from '../hooks/useHabitAdjustmentCheckin'
import {
  useGoalReviewPrompts,
} from '../hooks/useGoalReviewPrompts'
import {
  useSkillCheckpoints,
} from '../hooks/useSkillCheckpoints'
import {
  getCarryoverUX,
} from '../utils/carryoverCopy'
import {
  COPY as TODAY_COPY,
  deriveCoreComplete,
} from '../utils/todayOneClearThing'
import { TaskService, type CreateTaskRequest } from '../services/taskService'
import {
  usePreparedDaySnapshot,
} from '../components/prioritization/preparedDaySnapshot'
import TodayOneClearThing from './TodayOneClearThing'
import type { OneClearThingProps } from './TodayOneClearThing'
import type { TodayItemLike, FocusTaskItem, HabitOccurrenceItem } from './TodayFocusRefined'
import {
  TodaysReleaseCard,
} from '../components/prioritization/TodaysReleaseCard'
import {
  AutoMomReleaseOfferCard,
} from '../components/prioritization/AutoMomReleaseOfferCard'
import {
  PreparedDayCurtain,
} from '../components/automom/PreparedDayCurtain'
import {
  FillTheGapsCard,
} from '../components/prioritization/FillTheGapsCard'
import {
  AdmissionInbox,
} from '../components/admission/AdmissionInbox'
import type { OriAskKind } from '../utils/oriAsks'
import { useAdmissionProposals, useDecisionGaps, useStretch, useTodaysRelease } from '../hooks/useAsks'
import { QuietDaysCheckinCard } from '../components/today/QuietDaysCheckinCard'
import { useQuietDaysCheckin } from '../hooks/useQuietDaysCheckin'
import { executionWalk, orderByIds } from '../utils/dayShape'
import { selectCandidates } from '../utils/todayOneClearThing'
import { takeOn } from '../services/stretchService'
import {
  YanamasFlyleaf,
} from '../components/diary/YanamasFlyleaf'
import {
  YanamasCustomizer,
} from '../components/diary/YanamasCustomizer'

// Task Detail Modal interfaces

interface ActionItemDetail {
  id: number
  title: string
  description?: string
  status: string
  priority: string
  due_date?: string
  estimated_duration?: number
  progress?: number
  instance_id?: number  // For recurring task instances
}

interface TaskDetail {
  id: number
  title: string
  description?: string
  status: string
  priority: string
  progress: number
  due_date?: string
  estimated_duration?: number
  action_items: ActionItemDetail[]
  parent_goal_title?: string
  is_recurring?: boolean
}

type TimeRange = 'today' | '3days' | '7days'

interface TodayItem {
  id: number
  title: string
  description?: string
  type: 'task' | 'action_item' | 'event'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'in_progress' | 'completed'
  due_date?: string
  estimated_duration?: number // in minutes
  time_slot?: 'morning' | 'afternoon' | 'evening' | 'anytime'
  parent_title?: string
  source?: 'horton' | 'google_calendar'
  start_time?: string
  end_time?: string
  is_recurring?: boolean
  is_scheduled_today?: boolean
  scheduled_time_slot?: string
  progress?: number // 0-100, when known (orchestrator view)
}

interface DayProgress {
  total: number
  completed: number
  inProgress: number
  percentage: number
}

// New interfaces for orchestrator today view
interface FocusTask {
  task_id: string
  title: string
  description?: string
  status: string
  priority: string
  due_date?: string
  estimated_duration?: number
  is_recurring: boolean
  is_scheduled_today: boolean
  scheduled_time_slot?: string
  action_items: any[]
  parent_goal_id?: string
  parent_goal_title?: string
  progress?: number
  // Momentum-safe scheduling fields
  incremental?: boolean
  sessions_done?: number
  sessions_target?: number
  last_progress_at?: string
  progress_units?: number
  user_pinned_for_today?: boolean
  skipped_count?: number
  // Hard deadline fields
  deadline_type?: 'soft' | 'hard'
  deadline_consequence?: string
  deadline_risk_level?: 'low' | 'medium' | 'high' | 'critical'
  deadline_warning?: HardDeadlineWarning
}

// Momentum-safe scheduling interfaces
interface MomentumState {
  recovery_mode: 'none' | 'low_momentum'
  recovery_message: string
  effective_limit: number
  focus_reasoning: Record<string, string[]>
  in_progress_backlog: FocusTask[]
  backlog_summary: {
    pending: number
    in_progress: number
    skipped: number
    recurring_due_today: number
  }
}

interface RecoveryDecision {
  decision_id: string
  decision_scope: string
  effective_date: string
  recovery_needed: boolean
  severity: 'low' | 'medium' | 'high'
  intervention_type: 'light_adjust' | 'guided_replan' | 'goal_recovery'
  recommended_timing: 'next_today_open' | 'prepare_day' | 'planning_session' | 'immediate'
  prompt_mode: 'silent' | 'banner' | 'modal' | 'forced_planning'
  goal_risk: 'on_track' | 'watch' | 'at_risk'
  user_readiness: 'ready' | 'overloaded' | 'unknown'
  candidate_task_ids: number[]
  // F7 G16: days since the user's last recorded activity (absence signal)
  absence_days?: number | null
  // Tone-guarded copy composed by the backend from severity + absence_days +
  // readiness (shared_utilities/services/recovery_copy.py emits the flat
  // {title, body, why_now}; `banner` is accepted for forward compatibility).
  copy?: {
    title?: string
    body?: string
    why_now?: string
    banner?: { title?: string; body?: string; primaryLabel?: string }
  }
  status: 'pending' | 'shown' | 'acted' | 'dismissed' | 'snoozed' | 'resolved' | 'superseded' | 'expired'
}

/**
 * One row of `data.scheduled_events[]`, verbatim from today-view
 * (orchestrator_service.ScheduledEvent.to_dict). Every key the endpoint stamps
 * is declared here — including the six Phase 2 read-model keys, which used to
 * be absent from this interface AND from the mapper below, so the execution
 * timeline never saw a commitment or a plan role at all.
 */
interface ScheduledEvent {
  id: string
  title: string
  start_time: string
  end_time: string
  duration_minutes: number
  source: string
  task_id?: string
  event_type: string
  completed?: boolean
  /** Canonical item behind the block, for click-through and de-duplication. */
  item_id?: string | null
  item_type?: 'task' | 'habit' | null
  // ── Constitution §3 / §6 Phase 2 read model (see SCHEDULING_CONSTITUTION) ──
  commitment?: string | null
  commitment_source?: string | null
  block_source?: string | null
  plan_role?: string | null
  last_reconciled_at?: string | null
  replacement_count?: number | null
  /** Not stamped by today-view today; read defensively for external rows. */
  is_all_day?: boolean
  calendar_name?: string
}

// Hard deadline warning from backend
interface HardDeadlineWarning {
  level: 'error' | 'critical' | 'urgent' | 'warning' | 'info'
  title: string
  message: string
  consequence?: string
  days_remaining?: number
  days_overdue?: number
  task_id: string
}

interface HabitOccurrence {
  id: number | string
  habit_id: number | string
  master_id?: number | string
  date?: string
  title: string
  description?: string
  time_slot?: string
  duration_minutes: number
  status: string
  category?: string
  is_virtual?: boolean
  is_exception?: boolean
  minimum_viable_repetition?: string
  cue_or_anchor?: string
}

interface TodayViewData {
  focus_tasks: FocusTask[]
  daily_habits: FocusTask[]
  scheduled_recurring: FocusTask[]
  recurring_tasks: FocusTask[]
  scheduled_events: ScheduledEvent[]
  completed_tasks: FocusTask[]
  habit_occurrences?: HabitOccurrence[]
  daily_focus_limit: number
  focus_slots_used: number
  focus_slots_available: number
  total_scheduled_minutes: number
  hard_deadline_warnings?: HardDeadlineWarning[]
  release_offer?: ReleaseOffer
}

// Auto-Mom Release Policy: the single already-approved "want one more?" offer
// the orchestrator/release gate decided is humane to surface. Optional and
// display-only — Today view renders it but never computes it.
export interface ReleaseOffer {
  decision_id: number
  item_id: string | null
  title: string
  user_visible_reason: string
}



// Get icon for time slot

// Priority colors and labels
const priorityConfig = {
  urgent: { bg: 'bg-bad', text: 'text-on-brand', label: 'Must Do', ring: 'ring-bad' },
  high: { bg: 'bg-warn', text: 'text-on-brand', label: 'Important', ring: 'ring-warn' },
  medium: { bg: 'bg-brand', text: 'text-on-brand', label: 'Normal', ring: 'ring-brand' },
  low: { bg: 'bg-muted', text: 'text-on-brand', label: 'Nice to Have', ring: 'ring-muted' }
}

export const TodayFocus: React.FC = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const [items, setItems] = useState<TodayItem[]>([])
  const [completedYesterday, setCompletedYesterday] = useState<TodayItem[]>([])
  const [loading, setLoading] = useState(true)
  // Defect fix 2026-08-29: every Today fetch failure was swallowed (console
  // only), so a broken backend rendered "Your day is wide open" — a calm,
  // confident lie. An empty state means "we looked and there is nothing"; a
  // failed look must say so and offer a retry.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [focusMode, setFocusMode] = useState(false)
  const [currentFocusIndex, setCurrentFocusIndex] = useState(0)
  const [personality, setPersonality] = useState<'supportive_best_friend' | 'calm_older_sibling' | 'quiet_mentor'>('supportive_best_friend')
  // Pinned (owner decision 2026-08-24): the Today dashboard is today-only.
  // The 3D/7D range selector is gone; the range machinery below stays inert.
  const [timeRange] = useState<TimeRange>('today')
  const [, setCalendarEvents] = useState<any[]>([])
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false) // Track if initial fetch has completed

  useProvideFeedbackContext({ pageType: 'today_focus', extra: { timeRange } })

  // New state for orchestrator-based today view
  const [todayViewData, setTodayViewData] = useState<TodayViewData | null>(null)
  const [useOrchestratorView, setUseOrchestratorView] = useState(true) // Toggle between old/new view
  
  // Track overall daily productivity (persists through backfills)
  const [completedTodayCount, setCompletedTodayCount] = useState(0)
  // Bumped on every today-view refetch (completion backfill, prepared-day
  // adoption, manual refresh) so the page's prepared-day counters refetch —
  // they used to be fetched once at mount and never again (2026-08-29).
  const [planVersion, setPlanVersion] = useState(0)
  // True only for the beat a completion owns — see fetchTodayViewFromOrchestrator.
  const [askSuppressedByCompletion, setAskSuppressedByCompletion] = useState(false)
  const [dailyFocusLimit, setDailyFocusLimit] = useState(3) // Default matches backend default
  
  // Momentum-safe scheduling state
  const [momentumState, setMomentumState] = useState<MomentumState>({
    recovery_mode: 'none',
    recovery_message: '',
    effective_limit: 3,
    focus_reasoning: {},
    in_progress_backlog: [],
    backlog_summary: { pending: 0, in_progress: 0, skipped: 0, recurring_due_today: 0 }
  })
  
  // Daily check-in state
  const [dailyCheckInCompleted, setDailyCheckInCompleted] = useState(false)
  const [moodSliderValue, setMoodSliderValue] = useState<number | null>(null)
  // Today's answer and what the server says it changes. The question is only
  // worth asking because of `moodSuggestion` — see mood_service.py.
  const [dayMood, setDayMood] = useState<'low' | 'okay' | 'good' | 'great' | null>(null)
  const [moodSuggestion, setMoodSuggestion] = useState<string | null>(null)
  
  // Incomplete tasks handler state
  const [incompleteTasks, setIncompleteTasks] = useState<any[]>([])
  const [recoveryDecision, setRecoveryDecision] = useState<RecoveryDecision | null>(null)
  const [showIncompleteModal, setShowIncompleteModal] = useState(false)
  const [userState, setUserState] = useState<string>('steady')
  const [carryoverEntryMode, setCarryoverEntryMode] = useState<'banner' | 'modal' | 'disabled'>('banner')
  const [carryoverDismissed, setCarryoverDismissed] = useState(false)
  const [carryoverModalSuppressedKey, setCarryoverModalSuppressedKey] = useState<string | null>(null)
  const [carryoverProcessingTaskId, setCarryoverProcessingTaskId] = useState<string | null>(null)
  // Which carried-over task has its "why not today?" chips open (contract §5).
  const [carryoverReasonFor, setCarryoverReasonFor] = useState<number | null>(null)
  
  const [pinningTask, setPinningTask] = useState<string | null>(null)
  // Pinned true 2026-08-29: the legacy Today render (and the "New UI" button that
  // was its only setter) is gone, so there is nothing to toggle back to.
  const [useRefinedView] = useState(true)

  
  // Task detail modal state
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null)
  const [, setLoadingTaskDetail] = useState(false)
  const [, setTaskActionItems] = useState<ActionItemDetail[]>([])
  
  // Progress notes state
  
  // Focus mode quick note state
  const [focusModeNoteExpanded, setFocusModeNoteExpanded] = useState(false)
  const [focusModeNote, setFocusModeNote] = useState('')
  const [savingFocusNote, setSavingFocusNote] = useState(false)
  
  // Use server time for date calculations (supports time simulation testing)
  const { serverDate, serverDateString, isLoading: serverTimeLoading } = useServerTime()

  // Defect fix 2026-08-29: the fetch callbacks below keyed off `serverDate`,
  // which changes identity on every server-clock poll (and, before the hook
  // was memoised, on every render). That rebuilt getEndDate ->
  // fetchTodayItems -> fetchTodayViewFromOrchestrator, and the auto-refresh
  // effect cleared and recreated its interval before it could reach five
  // minutes — so the "refresh every 5 minutes" never once fired. The day
  // boundary only moves once a day, so key it off the date STRING.
  const dayStart = useMemo(
    () => startOfDay(new Date(`${serverDateString}T00:00:00`)),
    [serverDateString],
  )

  
  // Check if user is going above and beyond
  // MOMENTUM-SAFE: In low_momentum mode, use effective_limit as the baseline
  const effectiveGoal = momentumState.recovery_mode === 'low_momentum' 
    ? (momentumState.effective_limit || 1) 
    : dailyFocusLimit
  const isAboveAndBeyond = completedTodayCount > effectiveGoal
  const beyondCount = Math.max(0, completedTodayCount - effectiveGoal)
  
  // Calculate progress - use completedTodayCount to track real productivity
  const progress: DayProgress = (() => {
    if (useOrchestratorView && todayViewData) {
      // Phase 4 — the focus-limit denominator is tasks only. Habits
      // are recurrence-driven and don't consume the daily focus
      // budget, so folding them into `total` produced misleading
      // fractions (5 habits + 3-task limit used to render as "0/8").
      const focusTasks = todayViewData.focus_tasks
      const habits = todayViewData.daily_habits || todayViewData.recurring_tasks || []
      // MOMENTUM-SAFE: In low_momentum mode, use effective_limit as the goal
      // This ensures the progress bar and stats reflect the reduced expectation
      const isLowMomentum = momentumState.recovery_mode === 'low_momentum'
      const total = isLowMomentum
        ? (momentumState.effective_limit || 1)  // Use effective limit in recovery mode
        : (dailyFocusLimit || focusTasks.length)  // Otherwise use user's preference
      // Completed = actual completions today (persisted count)
      const completed = completedTodayCount
      // In-progress covers both focus tasks and habits because the
      // progress card is the user's single pulse of "what's moving
      // right now". The mismatch with `total` is intentional — `total`
      // is a *task budget*, inProgress is a *status signal*.
      const inProgress = [...focusTasks, ...habits].filter(
        t => t.status === 'in_progress'
      ).length
      return {
        total,
        completed,
        inProgress,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0
      }
    }
    // Defect fix 2026-08-29: `items` never carries completed rows here, so the
    // old `items.filter(status === 'completed')` was structurally always 0.
    // Use the same persisted count the orchestrator branch uses.
    return {
      total: items.length,
      completed: completedTodayCount,
      inProgress: items.filter(i => i.status === 'in_progress').length,
      percentage: items.length > 0 ? Math.round((completedTodayCount / items.length) * 100) : 0
    }
  })()
  
  // Get pending items sorted by priority
  // Use orchestrator data if available, otherwise fall back to items
  const pendingItems = (() => {
    if (useOrchestratorView && todayViewData && timeRange === 'today') {
      // Convert orchestrator tasks to TodayItem format for focus mode
      // Use daily_habits if available, fall back to recurring_tasks for backward compatibility
      const habits = todayViewData.daily_habits || todayViewData.recurring_tasks || []
      const allTasks = [
        ...todayViewData.focus_tasks.filter(t => t.status !== 'completed'),
        ...habits.filter(t => t.status !== 'completed')
      ]
      return allTasks
        .map(t => {
          // Safely parse task_id - it should be a numeric string like "17"
          const parsedId = parseInt(t.task_id, 10)
          if (isNaN(parsedId)) {
            console.warn(`[pendingItems] Invalid task_id: ${t.task_id} for task: ${t.title}`)
          }
          return {
            id: parsedId,
            title: t.title,
            type: 'task' as const,
            priority: (t.priority || 'medium') as 'urgent' | 'high' | 'medium' | 'low',
            status: (t.status || 'pending') as 'pending' | 'in_progress' | 'completed',
            description: t.description,
            progress: t.progress || 0,
            is_recurring: t.is_recurring
          }
        })
        .filter(t => !isNaN(t.id)) // Filter out any tasks with invalid IDs
        .sort((a, b) => {
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 }
          return priorityOrder[a.priority] - priorityOrder[b.priority]
        })
    }
    return items
      .filter(i => i.status !== 'completed')
      .sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 }
        return priorityOrder[a.priority] - priorityOrder[b.priority]
      })
  })()
  
  // Get yanamas' message
  
  
  // Load user personality and app preferences
  useEffect(() => {
    const loadPersonality = async () => {
      try {
        const profile = await profileService.getUserProfile()
        const approach = profile.horton_emotional_approach || 'love_first'
        const style = profile.horton_conversation_style || 'balanced'
        
        if (approach === 'love_first' && style === 'balanced') setPersonality('supportive_best_friend')
        else if (approach === 'context_first' && style === 'practical') setPersonality('calm_older_sibling')
        else if (approach === 'emotional_first' && style === 'exploratory') setPersonality('quiet_mentor')
        else setPersonality('supportive_best_friend')
      } catch (e) {
        console.log('Using default personality')
      }
    }
    
    const loadAppPreferences = async () => {
      try {
        const res = await fetch('/api/profile/app-preferences', { credentials: 'include' })
        const data = await res.json()
        if (data.success && data.preferences) {
          const mode = data.preferences.carryover_entry_mode
          if (mode === 'banner' || mode === 'modal' || mode === 'disabled') {
            console.log(`🔧 [Carryover] Loaded entry mode from app preferences: ${mode}`)
            setCarryoverEntryMode(mode)
          }
        }
      } catch (e) {
        console.log('Using default carryover entry mode (banner)')
      }
    }
    
    loadPersonality()
    loadAppPreferences()
  }, [])
  
  // Use coach alerts hook for alerts panel
  const { alerts } = useCoachAlerts()
  const habitAdjustment = useHabitAdjustmentCheckin()
  const goalReview = useGoalReviewPrompts()
  const skillCheckpoints = useSkillCheckpoints()

  // Motivation nudge REMOVED 2026-08-29: /api/orchestrator/nudge proxies to the
  // motivator's v1 pipeline, which is disabled under MOTIVATOR_MODE=super_mvp_v2
  // and answers 503. The hook set `fetched` in a finally, so the failure
  // rendered as "You're on track! No stuck items detected." — a check we never
  // made, reported to the user as a result.

  const carryoverDecisionKey = recoveryDecision
    ? `${recoveryDecision.decision_id}:${recoveryDecision.effective_date}:${incompleteTasks.map((task: any) => task.id).join(',')}`
    : null

  const updateRecoveryDecisionStatus = useCallback((status: RecoveryDecision['status']) => {
    setRecoveryDecision(prev => prev ? { ...prev, status } : prev)
    fetch('/api/orchestrator/recovery-decision/status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status, decision_scope: 'daily_recovery' })
    }).catch(console.warn)
  }, [])

  const closeCarryoverModal = useCallback((status: RecoveryDecision['status'] = 'snoozed') => {
    if (carryoverDecisionKey) setCarryoverModalSuppressedKey(carryoverDecisionKey)
    setShowIncompleteModal(false)
    updateRecoveryDecisionStatus(status)
  }, [carryoverDecisionKey, updateRecoveryDecisionStatus])
  
  // Decouple modal opening from fetch timing:
  // carryoverEntryMode loads asynchronously from the profile,
  // and incompleteTasks loads asynchronously from the API.
  // This effect fires whenever either changes, ensuring the modal
  // opens even if the mode arrives after the tasks (or vice versa).
  useEffect(() => {
    const promptMode = recoveryDecision?.prompt_mode
    const recoveryStatus = recoveryDecision?.status
    // 'acted' is NOT terminal. Defect found 2026-08-30 by
    // tests/ui-behavior/07-recovery-triage: the backend stamps the decision
    // `acted` the moment ONE task is actioned while others remain
    // (recovery_decision_service.py:452, which reserves `resolved` for the
    // empty-candidate case). This gate excluded `acted`, so answering one of
    // five carryovers closed the door on the other four for the rest of the
    // day — no modal, no way back in. 'acted' means the user engaged;
    // 'resolved' means they finished. Only the latter should end the day's
    // triage, and the `incompleteTasks.length > 0` condition below already
    // guarantees we never reopen over an empty list. Re-opening within a
    // session is separately damped by carryoverModalSuppressedKey.
    const statusAllowsAutoOpen =
      !recoveryStatus ||
      recoveryStatus === 'pending' ||
      recoveryStatus === 'shown' ||
      recoveryStatus === 'acted'
    const alreadyHandledThisDecision = carryoverDecisionKey && carryoverModalSuppressedKey === carryoverDecisionKey
    const shouldOpen = (
      promptMode === 'modal'
      || promptMode === 'forced_planning'
      || (carryoverEntryMode === 'modal' && recoveryDecision?.recovery_needed)
    )
    if (shouldOpen && statusAllowsAutoOpen && !alreadyHandledThisDecision && incompleteTasks.length > 0 && !showIncompleteModal) {
      console.log(`🔔 [Recovery] Auto-opening modal: ${incompleteTasks.length} tasks, mode=${promptMode}, preference=${carryoverEntryMode}`)
      setShowIncompleteModal(true)
    }
  }, [recoveryDecision, incompleteTasks, showIncompleteModal, carryoverEntryMode, carryoverDecisionKey, carryoverModalSuppressedKey])
  
  
  // Get the end date based on time range
  const getEndDate = useCallback((range: TimeRange): Date => {
    const today = dayStart
    switch (range) {
      case 'today':
        return addDays(today, 1)
      case '3days':
        return addDays(today, 3)
      case '7days':
        return addDays(today, 7)
    }
  }, [dayStart])

  // Fetch items based on time range
  const fetchTodayItems = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true)
      else setLoading(true)
      
      const today = dayStart
      const endDate = getEndDate(timeRange)
      const yesterday = addDays(today, -1)
      
      // Fetch tasks and calendar events
      // Note: Action items are NOT shown directly in Today view - they are only visible
      // when opening a task (action items belong to tasks, not shown standalone)
      const [tasksRes, calendarRes] = await Promise.all([
        fetch('/api/hierarchy/tasks', { credentials: 'include' }),
        fetch(`/api/schedule/events?include_google=true&start=${today.toISOString()}&end=${endDate.toISOString()}`, { credentials: 'include' })
      ])

      // Defect fix 2026-08-29: a non-2xx here used to fall through to an empty
      // day. Tasks are the spine of Today — if they didn't load, we don't know
      // what today holds and must say so instead of showing a blank day.
      if (!tasksRes.ok) {
        throw new Error(`tasks request failed (${tasksRes.status})`)
      }

      const tasksData = await tasksRes.json()
      
      // Process schedule/calendar events (includes both yanamas schedule events AND Google Calendar events)
      const scheduleEvents: any[] = []
      if (calendarRes.ok) {
        const calendarData = await calendarRes.json()
        if (calendarData.success && calendarData.events) {
          // Separate yanamas schedule events from Google Calendar events
          calendarData.events.forEach((event: any) => {
            scheduleEvents.push(event)
          })
        }
      }
      setCalendarEvents(scheduleEvents)
      
      const allItems: TodayItem[] = []
      const yesterdayItems: TodayItem[] = []
      
      // Process tasks - handle both array and {success, tasks} response formats
      const tasks = Array.isArray(tasksData) ? tasksData : (tasksData.tasks || [])
      tasks.forEach((task: any) => {
        // Skip completed tasks (unless completed yesterday for wins)
        if (task.status === 'completed') {
          if (task.updated_at) {
            const completedDate = new Date(task.updated_at)
            if (completedDate >= yesterday && completedDate < today) {
              yesterdayItems.push({
                id: task.id,
                title: task.title,
                description: task.description,
                type: 'task',
                priority: task.priority || 'medium',
                status: 'completed',
                due_date: task.due_date,
                estimated_duration: task.estimated_duration,
                time_slot: getTimeSlotFromDueDate(task.due_date)
              })
            }
          }
          return
        }
        
        const item: TodayItem = {
          id: task.id,
          title: task.title,
          description: task.description,
          type: 'task',
          priority: task.priority || 'medium',
          status: task.status || 'pending',
          due_date: task.due_date,
          estimated_duration: task.estimated_duration,
          time_slot: getTimeSlotFromDueDate(task.due_date)
        }
        
        // For "Today" view: include if due today, in progress, or high/urgent priority
        // For multi-day views: include if due within range OR no due date (show all pending)
        const dueDate = task.due_date ? new Date(task.due_date) : null
        const isDueInRange = dueDate && dueDate >= today && dueDate < endDate
        const isInProgress = task.status === 'in_progress'
        const isHighPriority = task.priority === 'urgent' || task.priority === 'high'
        const hasNoDueDate = !task.due_date
        
        if (timeRange === 'today') {
          // Today view: more focused - only show items that need attention today
          if (isDueInRange || isInProgress || isHighPriority) {
            allItems.push(item)
          }
        } else {
          // Multi-day view: show all items due in range + items without due dates
          if (isDueInRange || isInProgress || isHighPriority || hasNoDueDate) {
            allItems.push(item)
          }
        }
      })
      
      // Note: Action items are NOT displayed in the main Today view.
      // They are only visible when opening a task via the task detail modal.
      // This keeps the Today view focused on tasks (the parent level).
      
      // Add yanamas schedule events (but NOT schedule blocks for tasks - those are already in allItems)
      // Schedule blocks for tasks have a task_id or parent_task_id - skip those to avoid duplicates
      scheduleEvents.forEach((event: any) => {
        // Skip schedule blocks that are linked to tasks - they'll show up via the tasks list
        if (event.task_id || event.parent_task_id) {
          return
        }
        
        // Only add standalone events (no task association)
        if (event.source === 'horton' && event.type === 'event') {
          const item: TodayItem = {
            id: event.item_id || event.id,
            title: event.title,
            description: event.description,
            type: 'event',
            priority: 'medium', // Schedule events don't have priority
            status: event.status || 'pending',
            due_date: event.date,
            estimated_duration: event.duration,
            time_slot: event.time_slot,
            source: 'horton',
            start_time: event.start_time,
            end_time: event.end_time
          }
          allItems.push(item)
        }
      })
      
      // Sort items by due date
      allItems.sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0
        if (!a.due_date) return 1
        if (!b.due_date) return -1
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      })
      
      // Debug logging for multi-day views
      console.log(`📅 [TodayFocus] Time range: ${timeRange}`)
      console.log(`📅 [TodayFocus] Date range: ${today.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`)
      console.log(`📅 [TodayFocus] Total items fetched: ${allItems.length}`)
      
      // Count items by category
      const withDueDate = allItems.filter(i => i.due_date).length
      const withoutDueDate = allItems.filter(i => !i.due_date).length
      const inProgress = allItems.filter(i => i.status === 'in_progress').length
      const highPriority = allItems.filter(i => i.priority === 'urgent' || i.priority === 'high').length
      console.log(`📅 [TodayFocus] With due date: ${withDueDate}, No due date: ${withoutDueDate}, In progress: ${inProgress}, High priority: ${highPriority}`)
      
      setItems(allItems)
      setCompletedYesterday(yesterdayItems.slice(0, 5)) // Show max 5 yesterday wins
      setLoadError(null)

    } catch (error) {
      console.error('Error fetching today items:', error)
      // Defect fix 2026-08-29: hold the failure so the page can render an
      // honest error instead of a wide-open day.
      setLoadError("We couldn't load your day just now.")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [timeRange, getEndDate, dayStart])
  
  // Fetch from orchestrator endpoint (new approach).
  // `completionBackfill` marks the 500ms refetch a checkmark triggers. That one
  // beat belongs to the completion itself (doctrine: Core records -> Motivator
  // MAY celebrate -> Auto-Mom MAY release at most one optional item), so it is
  // not allowed to ALSO introduce a fresh ask. Every other caller passes
  // nothing and clears the suppression again. (2026-08-29)
  const fetchTodayViewFromOrchestrator = useCallback(async (isRefresh = false, completionBackfill = false) => {
    setAskSuppressedByCompletion(completionBackfill)
    if (timeRange !== 'today') {
      // For multi-day views, fall back to old method
      setUseOrchestratorView(false)
      return fetchTodayItems(isRefresh)
    }
    
    try {
      if (isRefresh) setRefreshing(true)
      else setLoading(true)
      
      // Use server date for time simulation support
      const today = serverDateString
      
      // Fetch both today-view and focus-preview for momentum-safe data
      const [todayViewRes, focusPreviewRes, checkInRes] = await Promise.all([
        fetch(`/api/orchestrator/today-view?date=${today}`, { credentials: 'include' }),
        fetch('/api/orchestrator/focus-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ date: today })
        }),
        fetch(`/api/orchestrator/daily-checkin?date=${today}`, { credentials: 'include' })
      ])
      
      // Check if check-in already completed today
      if (checkInRes.ok) {
        const checkInData = await checkInRes.json()
        setDailyCheckInCompleted(checkInData.completed || false)
        setDayMood(checkInData.mood ?? null)
        setMoodSuggestion(checkInData.suggested_because ?? null)
      }
      
      // Fetch user state (first load only — it moves on a slower clock)
      if (!isRefresh) {
        const userStateRes = await fetch('/api/orchestrator/user-state', { credentials: 'include' })
        if (userStateRes.ok) {
          const userStateData = await userStateRes.json()
          if (userStateData.success) {
            setUserState(userStateData.current_state || 'steady')
          }
        }
      }

      // Check for orchestrator recovery decision + candidate tasks. Defect fix
      // 2026-08-29: this was inside the `!isRefresh` block, so a refresh left
      // the carryover banner describing tasks the user had already resolved.
      {
        const decisionRes = await fetch('/api/orchestrator/recovery-decision?decision_scope=daily_recovery', { credentials: 'include' })
        if (decisionRes.ok) {
          const decisionData = await decisionRes.json()
          const decision: RecoveryDecision | null = decisionData?.decision || null
          setRecoveryDecision(decision)

          if (decision?.recovery_needed && Array.isArray(decision.candidate_task_ids) && decision.candidate_task_ids.length > 0) {
            const idsParam = decision.candidate_task_ids.join(',')
            const incompleteRes = await fetch(`/api/orchestrator/incomplete-tasks?task_ids=${idsParam}`, { credentials: 'include' })
            if (incompleteRes.ok) {
              const incompleteData = await incompleteRes.json()
              if (incompleteData.success && incompleteData.incomplete_tasks?.length > 0) {
                setIncompleteTasks(incompleteData.incomplete_tasks)
                // Mark shown when surfaced.
                fetch('/api/orchestrator/recovery-decision/status', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ status: 'shown', decision_scope: 'daily_recovery' })
                }).catch(console.warn)
              }
            }
          } else {
            setIncompleteTasks([])
          }
        }
      }
      
      if (!todayViewRes.ok) {
        console.warn('Orchestrator endpoint failed, falling back to legacy fetch')
        setUseOrchestratorView(false)
        return fetchTodayItems(isRefresh)
      }
      
      const data = await todayViewRes.json()
      
      // Process focus-preview for momentum-safe data
      if (focusPreviewRes.ok) {
        const focusData = await focusPreviewRes.json()
        if (focusData.success && focusData.data) {
          setMomentumState({
            recovery_mode: focusData.data.recovery_mode || 'none',
            recovery_message: focusData.data.recovery_message || '',
            effective_limit: focusData.data.effective_limit || 3,
            focus_reasoning: focusData.data.focus_reasoning || {},
            in_progress_backlog: focusData.data.in_progress_backlog || [],
            backlog_summary: focusData.data.backlog_summary || { pending: 0, in_progress: 0, skipped: 0, recurring_due_today: 0 }
          })
          console.log(`🚀 [Momentum] Mode: ${focusData.data.recovery_mode}, Limit: ${focusData.data.effective_limit}`)
        }
      }
      
      if (data.success && data.data) {
        console.log(`📋 [Backfill Debug] Orchestrator returned ${data.data.focus_tasks?.length || 0} focus tasks, ${data.data.daily_habits?.length || 0} daily habits, ${data.data.scheduled_recurring?.length || 0} scheduled recurring`)
        console.log(`📋 [Backfill Debug] Focus slots: ${data.data.focus_slots_used}/${data.data.daily_focus_limit}`)
        
        setTodayViewData(data.data)
        setUseOrchestratorView(true)
        setPlanVersion(v => v + 1)
        setLoadError(null)  // a good read clears any held failure (2026-08-29)
        
        // Set daily focus limit from the response
        if (data.data.daily_focus_limit) {
          setDailyFocusLimit(data.data.daily_focus_limit)
        }
        
        // Use the backend's completed_today_count for progress persistence.
        // This counts tasks with completion_date = today, so it persists across
        // refreshes. Defect fix 2026-08-29: this used to skip refreshes
        // (`!isRefresh`), freezing "N of M done" at its first-load value. The
        // optimistic +1 still gives the instant pop; this reconciles it.
        if (data.data.completed_today_count !== undefined) {
          setCompletedTodayCount(data.data.completed_today_count)
          console.log(`📊 [Progress] Restored completed count from backend: ${data.data.completed_today_count}`)
        }
        
        // Also fetch calendar events separately for the timeline
        const calRes = await fetch(`/api/schedule/events?include_google=true&start=${today}T00:00:00&end=${today}T23:59:59`, { 
          credentials: 'include' 
        })
        if (calRes.ok) {
          const calData = await calRes.json()
          if (calData.success && calData.events) {
            setCalendarEvents(calData.events)
          }
        }
      } else {
        console.warn('Orchestrator returned no data, falling back')
        setUseOrchestratorView(false)
        return fetchTodayItems(isRefresh)
      }
      
    } catch (error) {
      console.error('Error fetching from orchestrator:', error)
      setUseOrchestratorView(false)
      return fetchTodayItems(isRefresh)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [timeRange, fetchTodayItems, serverDateString])
  
  // Pin/unpin a task for today's focus
  const handlePinTask = useCallback(async (taskId: string, pinned: boolean) => {
    setPinningTask(taskId)
    try {
      const response = await fetch('/api/orchestrator/pin-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_id: parseInt(taskId), pinned })
      })
      
      if (response.ok) {
        // Refresh the view
        await fetchTodayViewFromOrchestrator(true)
      }
    } catch (error) {
      console.error('Error pinning task:', error)
    } finally {
      setPinningTask(null)
    }
  }, [fetchTodayViewFromOrchestrator])
  
  // Log progress on a task
  const handleLogProgress = useCallback(async (taskId: string, units: number = 1, notes: string = '') => {
    try {
      const response = await fetch('/api/orchestrator/log-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_id: parseInt(taskId), progress_units: units, notes })
      })
      
      if (response.ok) {
        // Refresh the view
        await fetchTodayViewFromOrchestrator(true)
      }
    } catch (error) {
      console.error('Error logging progress:', error)
    }
  }, [fetchTodayViewFromOrchestrator])
  
  // If a habit/task was just added elsewhere (e.g. the chat's "Make it real"),
  // refresh Today's Focus on arrival so it can surface immediately instead of
  // waiting for the user to hit the recompute button.
  useEffect(() => {
    if (sessionStorage.getItem('horton_refresh_todays_focus')) {
      sessionStorage.removeItem('horton_refresh_todays_focus')
      void fetchTodayViewFromOrchestrator(true)
    }
  }, [fetchTodayViewFromOrchestrator])

  // Get reasoning tags for a task
  const getReasoningTags = useCallback((taskId: string): string[] => {
    return momentumState.focus_reasoning[taskId] || []
  }, [momentumState.focus_reasoning])
  
  // Helper to render reasoning tag badge
  
  // Load task detail with action items
  const loadTaskDetail = useCallback(async (taskId: string | number) => {
    console.log('🔍 [loadTaskDetail] Loading task:', taskId)
    setLoadingTaskDetail(true)
    try {
      // Fetch task details
      const taskRes = await fetch(`/api/hierarchy/tasks/${taskId}`, {
        credentials: 'include'
      })
      
      console.log('🔍 [loadTaskDetail] Task API response status:', taskRes.status)
      
      if (!taskRes.ok) {
        console.error('Failed to load task details, status:', taskRes.status)
        setLoadingTaskDetail(false)
        return
      }
      
      const taskData = await taskRes.json()
      console.log('🔍 [loadTaskDetail] Task data received:', taskData)
      
      // The API returns { item: {...}, children: {...}, parent_chain: [...] }
      const task = taskData.item
      const children = taskData.children
      
      if (task && task.id) {
        // Action items are already included in the response under children.action_items
        const rawItems = children?.action_items || []
        const isRecurring = task.is_recurring || task.is_habit
        const today = serverDateString
        
        // For recurring tasks, fetch today's progress for each action item
        let actionItems: ActionItemDetail[] = []
        
        if (isRecurring && rawItems.length > 0) {
          // Use new recurring instances API to get today's instances for all action items
          try {
            const instancesRes = await fetch(`/api/recurring-instances/task/${task.id}/action-items/today`, {
              credentials: 'include'
            })
            
            if (instancesRes.ok) {
              const instancesData = await instancesRes.json()
              if (instancesData.success && instancesData.action_item_instances) {
                actionItems = instancesData.action_item_instances.map((item: any) => ({
                  id: item.action_item.id,
                  title: item.action_item.title,
                  description: item.action_item.description,
                  status: item.instance.status || 'pending',
                  priority: item.action_item.priority || 'medium',
                  due_date: item.action_item.due_date,
                  estimated_duration: item.action_item.estimated_duration,
                  progress: item.instance.progress || 0,
                  instance_id: item.instance.id  // Store instance ID for updates
                }))
                console.log('🔄 [loadTaskDetail] Loaded today\'s instances from recurring API:', actionItems.length, 'items')
              }
            }
          } catch (e) {
            console.log('Could not fetch from recurring instances API, falling back to progress history')
            // Fallback to old method
            const progressPromises = rawItems.map(async (ai: any) => {
              try {
                const progressRes = await fetch(`/api/progress/item/action_item/${ai.id}/history`, {
                  credentials: 'include'
                })
                if (progressRes.ok) {
                  const progressData = await progressRes.json()
                  const todayRecords = (progressData.history || progressData.records || []).filter((r: any) => 
                    r.progress_date === today
                  )
                  if (todayRecords.length > 0) {
                    return todayRecords[todayRecords.length - 1].progress_value || 0
                  }
                }
              } catch (e) {
                console.log('Could not fetch progress for action item', ai.id)
              }
              return 0
            })
            
            const todayProgressValues = await Promise.all(progressPromises)
            
            actionItems = rawItems.map((ai: any, idx: number) => ({
              id: ai.id,
              title: ai.title,
              description: ai.description,
              status: todayProgressValues[idx] >= 100 ? 'completed' : (todayProgressValues[idx] > 0 ? 'in_progress' : 'pending'),
              priority: ai.priority || 'medium',
              due_date: ai.due_date,
              estimated_duration: ai.estimated_duration,
              progress: todayProgressValues[idx]
            }))
          }
        } else if (actionItems.length === 0) {
          // Non-recurring task - use stored progress
          actionItems = rawItems.map((ai: any) => ({
            id: ai.id,
            title: ai.title,
            description: ai.description,
            status: ai.status || 'pending',
            priority: ai.priority || 'medium',
            due_date: ai.due_date,
            estimated_duration: ai.estimated_duration,
            progress: ai.progress || 0
          }))
        }
        
        console.log('🔍 [loadTaskDetail] Setting selectedTask:', task.title, 'with', actionItems.length, 'action items', isRecurring ? '(recurring - using today\'s progress)' : '')
        setSelectedTask({
          id: task.id,
          title: task.title,
          description: task.description,
          status: task.status || 'pending',
          priority: task.priority || 'medium',
          progress: task.progress || 0,
          due_date: task.due_date || task.target_date,
          estimated_duration: task.estimated_duration,
          action_items: actionItems,
          parent_goal_title: task.parent_goal_title || task.goal_title,
          is_recurring: task.is_recurring || task.is_habit
        })
        setTaskActionItems(actionItems)
      } else {
        console.error('🔍 [loadTaskDetail] Task data missing id:', taskData)
      }
    } catch (error) {
      console.error('Error loading task detail:', error)
    } finally {
      setLoadingTaskDetail(false)
    }
  }, [serverDateString])
  
  // Toggle action item completion
  
  // Close task detail modal
  
  // Save progress note for an action item
  
  // Fetch notes history for an action item
  
  // Toggle expanded action item for progress notes
  
  // Initial load - wait for server time to be loaded before fetching
  // This ensures we use the correct server date (for time simulation testing)
  useEffect(() => {
    // Wait for server time to load before making initial fetch
    // This prevents using the wrong client date when server time differs
    if (serverTimeLoading) {
      return
    }
    
    // Only fetch if not already loaded (prevents double-fetch)
    if (!hasInitiallyLoaded) {
      console.log(`📅 [TodayFocus] Server time loaded, fetching for date: ${serverDateString}`)
      fetchTodayViewFromOrchestrator()
      setHasInitiallyLoaded(true)
    }
  }, [serverTimeLoading, serverDateString, hasInitiallyLoaded, fetchTodayViewFromOrchestrator])
  
  // Refetch when time range changes (after initial load)
  useEffect(() => {
    // Skip initial run since we handle it above with the mount effect
    if (!hasInitiallyLoaded) {
      return
    }
    
    // Use orchestrator for today, legacy for multi-day
    if (timeRange === 'today') {
      fetchTodayViewFromOrchestrator()
    } else {
      fetchTodayItems()
    }
  }, [timeRange]) // Only depend on timeRange - triggers when user clicks 3 Days, 7 Days, Today
  
  // Auto-refresh every 5 minutes.
  // Defect fix 2026-08-29: this effect depended on the two fetch callbacks,
  // whose identities changed on every render (see `dayStart` above). The page
  // re-renders at least once a minute from the pollers, so the interval was
  // torn down and rebuilt long before 5 minutes elapsed and NEVER fired. The
  // interval is now created once; a ref carries the current fetcher. The
  // refresh takes the CHEAP path (isRefresh=true), which skips the user-state
  // read and preserves the completed-today count.
  const autoRefreshRef = useRef<() => void>(() => {})
  autoRefreshRef.current = () => {
    if (timeRange === 'today') {
      void fetchTodayViewFromOrchestrator(true)
    } else {
      void fetchTodayItems(true)
    }
  }
  useEffect(() => {
    const interval = setInterval(() => autoRefreshRef.current(), 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])
  
  // Helper to determine time slot from due date
  const getTimeSlotFromDueDate = (dueDate?: string): 'morning' | 'afternoon' | 'evening' | 'anytime' => {
    if (!dueDate) return 'anytime'
    const date = new Date(dueDate)
    const hour = date.getHours()
    if (hour < 12) return 'morning'
    if (hour < 18) return 'afternoon'
    return 'evening'
  }
  
  // Group items by day for multi-day view
  
  // Get friendly day label (using server time for "today"/"tomorrow" comparison)
  
  // Get calendar events for a specific day
  
  // Does THIS completion land the day's celebration?
  //
  // Doctrine (2026-08-29): completion order is Core records -> Motivator MAY
  // celebrate -> Auto-Mom MAY release at most one optional item, and the
  // celebration lands ALONE. One checkmark used to fan out to six surfaces in
  // the same instant — an optimistic bump, a goal-review refetch, two action
  // toasts, and a 500ms backfill that could surface a release offer — while
  // TodayOneClearThing independently flipped `coreComplete` and fired confetti
  // plus permission-to-stop. Three of those demanded a decision on top of the
  // moment that was supposed to be a rest.
  //
  // Evaluated against the state this completion is ABOUT to produce, because
  // the optimistic setState has not flushed when the handler needs the answer.
  // Uses the SAME helper TodayOneClearThing gates confetti with, so the two
  // cannot drift into disagreeing about what is on screen.
  const willCelebrateAfter = useCallback(
    (completed: { taskId?: number; occurrenceId?: number | string }): boolean => {
      const isTask = completed.taskId !== undefined
      const focusTasks = (todayViewData?.focus_tasks || []).filter(t => t.status !== 'completed')
      const habitsList = todayViewData?.daily_habits || todayViewData?.recurring_tasks || []
      const occurrences = todayViewData?.habit_occurrences || []
      const isJustCompleted = (taskId: string) => isTask && parseInt(taskId, 10) === completed.taskId
      const remainingFocus = focusTasks.filter(t => !isJustCompleted(t.task_id))
      const limit = momentumState.effective_limit
      const effectiveFocus = momentumState.recovery_mode === 'low_momentum'
        ? remainingFocus.slice(0, limit)
        : remainingFocus
      return deriveCoreComplete({
        focusCount: focusTasks.length,
        completedTaskCount: (todayViewData?.completed_tasks?.length || 0) + (isTask ? 1 : 0),
        progressCompleted: completedTodayCount + (isTask ? 1 : 0),
        effectiveLimit: limit,
        remainingEffectiveFocus: effectiveFocus.length,
        totalHabits: habitsList.length + occurrences.length,
        completedHabits:
          habitsList.filter(h => h.status === 'completed' || isJustCompleted(h.task_id)).length +
          occurrences.filter(o => o.status === 'completed' || o.id === completed.occurrenceId).length,
      })
    },
    [todayViewData, momentumState, completedTodayCount],
  )

  // Toggle item completion
  const toggleItemComplete = async (item: TodayItem) => {
    const newStatus = item.status === 'completed' ? 'pending' : 'completed'
    
    // Determine the correct endpoint based on item type
    let endpoint: string
    if (item.type === 'task') {
      endpoint = `/api/hierarchy/tasks/${item.id}`
    } else if (item.type === 'action_item') {
      endpoint = `/api/hierarchy/action-items/${item.id}`
    } else {
      // Events cannot be completed via this method - they're calendar events
      console.warn(`Cannot toggle completion for item type: ${item.type}`)
      return
    }
    
    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      })
      
      if (response.ok) {
        const result = await response.json()
        
        // Update completed today count (only increment, never decrement - we track real productivity!)
        if (newStatus === 'completed') {
          setCompletedTodayCount(prev => prev + 1)
        }
        // Note: We don't decrement when uncompleting because we want to track actual productivity
        
        // Update items state (for legacy view)
        setItems(prev => prev.map(i => 
          i.id === item.id && i.type === item.type 
            ? { ...i, status: newStatus }
            : i
        ))
        
        // Also update todayViewData if in orchestrator mode
        if (useOrchestratorView && todayViewData) {
          setTodayViewData(prev => {
            if (!prev) return prev
            
            // Find the task being updated (check focus, daily_habits, and legacy recurring_tasks)
            const completedFromFocus = prev.focus_tasks.find(t => parseInt(t.task_id) === item.id)
            const completedFromHabits = prev.daily_habits?.find(t => parseInt(t.task_id) === item.id)
            const completedFromRecurring = prev.recurring_tasks?.find(t => parseInt(t.task_id) === item.id)
            const completedTask = completedFromFocus || completedFromHabits || completedFromRecurring

            // If completing a task, move it to completed_tasks
            if (newStatus === 'completed' && completedTask) {
              return {
                ...prev,
                focus_tasks: prev.focus_tasks.filter(t => parseInt(t.task_id) !== item.id),
                daily_habits: (prev.daily_habits || []).filter(t => parseInt(t.task_id) !== item.id),
                recurring_tasks: (prev.recurring_tasks || []).filter(t => parseInt(t.task_id) !== item.id),
                completed_tasks: [...(prev.completed_tasks || []), { ...completedTask, status: 'completed' }],
              }
            }

            // Otherwise, just update the status in place
            return {
              ...prev,
              focus_tasks: prev.focus_tasks.map(t =>
                parseInt(t.task_id) === item.id
                  ? { ...t, status: newStatus }
                  : t
              ),
              daily_habits: (prev.daily_habits || []).map(t =>
                parseInt(t.task_id) === item.id
                  ? { ...t, status: newStatus }
                  : t
              ),
              recurring_tasks: (prev.recurring_tasks || []).map(t =>
                parseInt(t.task_id) === item.id
                  ? { ...t, status: newStatus }
                  : t
              ),
            }
          })
        }
        
        // Handle cascade completion response - show info about what was auto-completed
        if (result.data?.cascade_completed && newStatus === 'completed') {
          const cascade = result.data.cascade_completed
          if (cascade.action_items > 0) {
            console.log(`✅ Also completed ${cascade.action_items} action item(s) under this task`)
            // Optionally show a toast notification here
          }
        }
        
        // Handle auto-completion from action item completing all items under a task
        if (result.data?.auto_completed && result.data.auto_completed.length > 0) {
          console.log(`✅ Auto-completed parents:`, result.data.auto_completed)
        }

        // AutoMom may have raised a goal-review prompt for the parent goal.
        // Sequencing fix 2026-08-29: this used to refetch immediately, so a
        // goal-review card (a question) could appear in the same beat as the
        // completion and the celebration. useGoalReviewPrompts already polls
        // once a minute; the card lands after the moment, not on top of it.

        // Doctrine gate (2026-08-29): when this completion lands the
        // celebration, the celebration lands alone — the two action toasts
        // below both ask for a decision and are suppressed for this beat.
        const celebrating = newStatus === 'completed' && willCelebrateAfter({ taskId: item.id })

        // Journey step unlocked: show action toast with scheduling link
        if (!celebrating && newStatus === 'completed' && result.data?.journey_step_unlocked) {
          const unlocked = result.data.journey_step_unlocked
          toast.toast({
            variant: 'info',
            title: 'Next step unlocked',
            description: unlocked.title,
            action: {
              label: 'Schedule Now',
              onClick: () => navigate('/review-schedule', {
                state: { items: [{ id: unlocked.id, type: 'task' as const, title: unlocked.title, priority: 'medium', estimated_duration: unlocked.estimated_duration || 30 }] }
              })
            }
          })
        }

        // "Completing this also helps X" — surface a held/saved thought this
        // completion moves forward (fire-and-forget, silent when none).
        if (!celebrating && newStatus === 'completed' && item.title) {
          void connectionForCompletedItem({ type: item.type, title: item.title }).then((cn) => {
            if (!cn) return
            toast.toast({
              variant: 'info',
              title: `Completing this also helps "${cn.thread.title}"`,
              description: cn.why,
              action: { label: 'See it', onClick: () => navigate('/horton-inbox') },
            })
          })
        }

        // BACKFILL: Always refresh the view after completing a task to get a new backfilled task
        // Use a small delay for better UX (let the completion animation play)
        if (newStatus === 'completed') {
          console.log(`✅ [Backfill] Task ${item.id} completed. Refreshing in 500ms to backfill...`)
          setTimeout(() => {
            console.log(`🔄 [Backfill] Triggering refresh now...`)
            if (useOrchestratorView) {
              // completionBackfill=true: this refetch belongs to the
              // completion, so it may refresh the plan but must not introduce
              // a new ask (release offer) in the same beat (2026-08-29).
              fetchTodayViewFromOrchestrator(true, true) // isRefresh=true to preserve completedTodayCount
            } else {
              fetchTodayItems(true)
            }
          }, 500) // 500ms delay for smooth UX
        }
      }
    } catch (error) {
      console.error('Error updating item:', error)
    }
  }
  
  // Start working on item
  const startItem = async (item: TodayItem) => {
    // Determine the correct endpoint based on item type
    let endpoint: string
    if (item.type === 'task') {
      endpoint = `/api/hierarchy/tasks/${item.id}`
    } else if (item.type === 'action_item') {
      endpoint = `/api/hierarchy/action-items/${item.id}`
    } else {
      // Events cannot be started via this method - they're calendar events
      console.warn(`Cannot start item type: ${item.type}`)
      return
    }
    
    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'in_progress' })
      })
      
      if (response.ok) {
        // Update items state (for legacy view)
        setItems(prev => prev.map(i => 
          i.id === item.id && i.type === item.type 
            ? { ...i, status: 'in_progress' }
            : i
        ))
        
        // Also update todayViewData if in orchestrator mode
        if (useOrchestratorView && todayViewData) {
          setTodayViewData(prev => {
            if (!prev) return prev
            return {
              ...prev,
              focus_tasks: prev.focus_tasks.map(t =>
                parseInt(t.task_id) === item.id
                  ? { ...t, status: 'in_progress' }
                  : t
              ),
              daily_habits: (prev.daily_habits || []).map(t =>
                parseInt(t.task_id) === item.id
                  ? { ...t, status: 'in_progress' }
                  : t
              ),
              recurring_tasks: (prev.recurring_tasks || []).map(t =>
                parseInt(t.task_id) === item.id
                  ? { ...t, status: 'in_progress' }
                  : t
              ),
            }
          })
        }
      }
    } catch (error) {
      console.error('Error starting item:', error)
    }
  }
  
  // Focus mode navigation
  const nextFocusItem = () => {
    if (currentFocusIndex < focusQueue.length - 1) {
      setCurrentFocusIndex(prev => prev + 1)
    }
  }
  
  const prevFocusItem = () => {
    if (currentFocusIndex > 0) {
      setCurrentFocusIndex(prev => prev - 1)
    }
  }
  
  
  // ItemCard deleted 2026-08-29 with the legacy Today render, its only caller.
  // It also carried the aria-label "Mark as incomplete" (no-shaming rule).
  
  // How many carryover tasks may actually be SURFACED as a banner. Prompt mode
  // is decided by backend policy; a local dismiss only suppresses low/medium
  // banner surfaces. Hoisted 2026-08-29 — the refined page rendered the banner
  // on any non-empty incompleteTasks, ignoring every gate below.
  const effectiveCarryoverCount = useMemo(() => {
    const promptMode = recoveryDecision?.prompt_mode
    const isHighRisk = recoveryDecision?.severity === 'high'
    const suppressed = (
      promptMode === 'silent'
      || (!isHighRisk && carryoverDismissed)
      || promptMode === 'modal'
      || promptMode === 'forced_planning'
      || (carryoverEntryMode === 'modal' && recoveryDecision?.recovery_needed)
    )
    return suppressed ? 0 : incompleteTasks.length
  }, [recoveryDecision, carryoverDismissed, carryoverEntryMode, incompleteTasks])

  // ── Auto-Mom ask sequencing (doctrine, 2026-08-29) ───────────────────────
  // Auto-Mom proposals are governed by RESTRAINT: the system asks for little,
  // and never twice for the same thing. Both gates below read real state.
  //
  // (a) Triage of the past resolves before a choice about the future. While a
  //     recovery decision is still unanswered AND actually has candidate
  //     tasks, the prepared-day curtain and the release offers stay closed:
  //     today's plan is built lazily on GET from a task set this very triage
  //     is about to change, so proposing from it would be proposing from
  //     numbers the user is in the middle of rewriting.
  const recoveryTriagePending = useMemo(() => {
    const status = recoveryDecision?.status
    const unanswered = !status || status === 'pending' || status === 'shown'
    return Boolean(recoveryDecision?.recovery_needed) && unanswered && incompleteTasks.length > 0
  }, [recoveryDecision, incompleteTasks])

  // (b) The curtain is the PULL menu of extra work. While it still holds
  //     un-actioned live entries, pushing "want one more?" on top of it is the
  //     same offer a second time. Not symmetric: the curtain is never
  //     suppressed by a release offer. Shared passive read — no extra request,
  //     and the same subordination TodaysReleaseCard already applies to
  //     itself, including its rule that only a KNOWN-silent curtain counts (a
  //     failed look is not an empty plan).
  const preparedDay = usePreparedDaySnapshot(planVersion)
  const curtainIsSilent = preparedDay.status === 'ready' && preparedDay.liveCount === 0

  // Rule 0 of the Do card's selector: the prepared day's top live pick.
  // Lifted here so focus mode and the Do card rank from the SAME input.
  const preparedTopItemId = recoveryTriagePending ? null
    : (preparedDay.liveItemIds.length > 0 ? String(preparedDay.liveItemIds[0]) : null)

  // Focus mode's walk order (owner, 2026-09-11: it led with the 15:45 task
  // while Do led with 14:15). `pendingItems` sorts by priority; the Do card
  // ranks with `selectCandidates`. One ranking, applied to both: rank the
  // focus tasks exactly as the card does, then order the walk by those ids.
  // Habits and anything the ranking did not name follow, untouched.
  const focusQueue = useMemo(() => {
    const tasks = (todayViewData?.focus_tasks ?? []).filter(t => t.status !== 'completed')
    const ranked = selectCandidates(
      tasks.map(t => ({
        task_id: String(t.task_id),
        status: t.status,
        estimated_duration: t.estimated_duration,
        scheduled_time_slot: t.scheduled_time_slot,
        user_pinned_for_today: (t as { user_pinned_for_today?: boolean }).user_pinned_for_today,
        deadline_type: (t as { deadline_type?: 'soft' | 'hard' }).deadline_type,
        reasoning_tags: (t as { reasoning_tags?: string[] }).reasoning_tags ?? [],
      })),
      new Date().getHours(),
      preparedTopItemId,
    )
    // The same walk the Do card shows (North Star rule 5): the person's own
    // leads first, then the clock, then what has no clock.
    const walk = executionWalk(ranked, c => c.task.scheduled_time_slot,
      c => c.why === 'Already started' || c.why === 'Pinned by you' || c.why === 'Due soon')
      .map(c => c.task.task_id)
    return orderByIds(pendingItems, walk, it => String(it.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayViewData?.focus_tasks, pendingItems.length, preparedTopItemId])

  // ── EVERY HOOK ABOVE THIS LINE. ──────────────────────────────────────────
  // The focus-mode branch below RETURNS EARLY. A hook placed after it runs on
  // the normal render and not on the focus-mode one, and React throws error 300
  // ("rendered fewer hooks than expected") the moment Focus mode is clicked —
  // which is exactly what happened on 2026-09-11 when seven hooks for the
  // ask zone were added below it. They now live here, above the return.
  // Everything already on today's plate. The curtain draws from the same pool
  // as the focus list, so without this it re-offers the plan as if it were a
  // set of extras — including the very task "Start here" is naming.
  const onThePlate = useMemo(() => {
    const ids: number[] = []
    const add = (v: unknown) => {
      const n = Number(v)
      if (Number.isFinite(n)) ids.push(n)
    }
    // Focus is what "Start here" draws from…
    for (const t of (todayViewData?.focus_tasks ?? [])) add(t.task_id)
    for (const t of (todayViewData?.recurring_tasks ?? [])) add(t.task_id)
    // …and the SCHEDULED blocks are the rest of the plate. Without these the
    // curtain kept offering a task that is on today's calendar and listed under
    // "The rest of today" — the same duplication one level down, and still
    // captioned "hasn't had attention lately" about work due at 07:45.
    for (const e of (todayViewData?.scheduled_events ?? [])) {
      if (e.item_type === 'task') add(e.item_id)
    }
    return ids
  }, [todayViewData?.focus_tasks, todayViewData?.recurring_tasks,
      todayViewData?.scheduled_events])

  // What the curtain will ACTUALLY offer once the plate is excluded — the ask
  // zone must count that, not the raw snapshot, or "N to answer" promises rows
  // the curtain will not draw.
  const curtainOffers = useMemo(() => {
    if (preparedDay.status !== 'ready') return null
    const plate = new Set(onThePlate)
    return preparedDay.liveItemIds.filter(id => !plate.has(id)).length
  }, [preparedDay.status, preparedDay.liveItemIds, onThePlate])

  // Today's optional slots and what could fill them — the ladder has granted
  // them since it shipped and nothing ever offered anything to put in one.
  const stretch = useStretch()
  const admission = useAdmissionProposals()
  const gaps = useDecisionGaps()
  const todaysRelease = useTodaysRelease(!curtainIsSilent)
  const quietCheckin = useQuietDaysCheckin()

  const extraAsks = useMemo(() => {
    const out: Array<{ key: string; kind: OriAskKind; node: React.ReactNode; empty?: boolean }> = []
    const push = (
      key: string, kind: OriAskKind, count: number | null, node: React.ReactNode,
    ) => out.push({ key, kind, node, empty: count === 0 })

    // Structural: ignoring one has a cost — the item stays in
    // pending_admission and never reaches the map. The card splits
    // decompose / trade-off / needs-a-detail across its own rows.
    push('admission-inbox', 'break_down', admission.count, (
      <AdmissionInbox proposals={admission.data} onResolved={() => void admission.refresh()} />
    ))
    if (!recoveryTriagePending) {
      // Feature 4: the prepared plan behind the curtain (pull surface). Work
      // you could actually do today, so it leads.
      // `curtainOffers` is null while the snapshot is still loading, which is
      // not the same as an empty curtain.
      push('prepared-day', 'offer', curtainOffers, (
        <PreparedDayCurtain
          alreadyOnThePlate={onThePlate}
          onAdopted={() => fetchTodayViewFromOrchestrator(true)}
        />
      ))
      // Names "Today's one move" from a LifeMap-weighted pool while the hero
      // card names its own pick from a different pool — two engines answering
      // one question, which doctrine does not allow. Demoted to a suggestion
      // until its decision is wired INTO the hero selector as rule 0.
      push('todays-release', 'offer', todaysRelease.count, (
        <TodaysReleaseCard release={todaysRelease.data} onChanged={() => void todaysRelease.refresh()} />
      ))
      if (curtainIsSilent && !askSuppressedByCompletion) {
        push('release-offer', 'offer', todayViewData?.release_offer ? 1 : 0, (
          <AutoMomReleaseOfferCard
            releaseOffer={todayViewData?.release_offer}
            onResolved={() => fetchTodayViewFromOrchestrator(true)}
          />
        ))
      }
    }
    // After two or more quiet days: the workload question, one tap to answer.
    // A decision that changes today (lighten / capture), so it ranks with them.
    push('quiet-days', 'decide', quietCheckin.count, (
      <QuietDaysCheckinCard checkin={quietCheckin.data} onAnswered={() => void quietCheckin.refresh()} />
    ))
    // One caring question when a must-have decision signal is missing or only
    // a weak guess. It blocks nothing, so it sits below the structural asks.
    push('fill-the-gaps', 'quick_answer', gaps.count, <FillTheGapsCard gaps={gaps.data} />)
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryTriagePending, askSuppressedByCompletion, todayViewData?.release_offer,
      admission.data, admission.count, gaps.data, gaps.count,
      todaysRelease.data, todaysRelease.count, curtainOffers, onThePlate, curtainIsSilent,
      quietCheckin.data, quietCheckin.count])


  // Focus Mode View
  if (focusMode && focusQueue.length > 0) {
    const focusItem = focusQueue[Math.min(currentFocusIndex, focusQueue.length - 1)]
    
    return (
      <div className="min-h-screen bg-primary flex flex-col items-center justify-center p-6">
        {/* Exit Focus Mode */}
        <button
          onClick={() => setFocusMode(false)}
          className="absolute top-6 right-6 p-3 rounded-full bg-surface/10 hover:bg-surface/20 text-on-brand transition-all"
        >
          <RotateCcw className="w-6 h-6" />
        </button>
        
        {/* Progress indicator */}
        <div className="text-on-brand/60 text-sm mb-4">
          {Math.min(currentFocusIndex, focusQueue.length - 1) + 1} of {focusQueue.length} items
        </div>
        
        {/* Focus Card */}
        <div className="w-full max-w-lg">
          <div className="bg-surface rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <span className={`
                inline-block px-4 py-2 rounded-full text-sm font-bold mb-4
                ${priorityConfig[focusItem.priority].bg} ${priorityConfig[focusItem.priority].text}
              `}>
                {priorityConfig[focusItem.priority].label}
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-3">
                {focusItem.title}
              </h2>
              {focusItem.description && (
                <p className="text-secondary">
                  {focusItem.description}
                </p>
              )}
            </div>
            
            {/* Large action buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => {
                  toggleItemComplete(focusItem)
                  if (currentFocusIndex < focusQueue.length - 1) {
                    setTimeout(nextFocusItem, 300)
                  } else {
                    setFocusMode(false)
                  }
                }}
                className="w-full h-16 text-lg bg-ok hover:opacity-90"
              >
                <CheckCircle2 className="w-6 h-6 mr-3" />
                Mark Complete
              </Button>
              
              {focusItem.status !== 'in_progress' && (
                <Button
                  onClick={() => startItem(focusItem)}
                  variant="outline"
                  className="w-full h-14 text-base"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start Working
                </Button>
              )}
              
              {/* Quick Note Toggle */}
              <button
                onClick={() => setFocusModeNoteExpanded(!focusModeNoteExpanded)}
                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl transition-all text-sm ${
                  focusModeNoteExpanded
                    ? 'bg-brand-subtle text-brand'
                    : 'text-secondary hover:text-brand hover:bg-brand-subtle'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                {focusModeNoteExpanded ? 'Hide Quick Note' : 'Add Quick Note'}
              </button>
              
              {/* Expandable Quick Note Input */}
              {focusModeNoteExpanded && (
                <div className="p-3 bg-surface-alt rounded-xl space-y-2 animate-in slide-in-from-top-2 duration-200">
                  <textarea
                    value={focusModeNote}
                    onChange={(e) => setFocusModeNote(e.target.value)}
                    placeholder="Quick note... (what you're working on, any blockers?)"
                    className="w-full px-3 py-2 text-sm border border-soft rounded-lg bg-surface text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand resize-none"
                    rows={2}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setFocusModeNote('')
                        setFocusModeNoteExpanded(false)
                      }}
                      className="px-3 py-1.5 text-xs text-secondary hover:text-primary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!focusModeNote.trim()) return
                        if (!focusItem.id || isNaN(focusItem.id)) {
                          console.error('Invalid task ID:', focusItem.id, 'for task:', focusItem.title)
                          alert('Cannot save note: Invalid task ID. Please refresh the page.')
                          return
                        }
                        setSavingFocusNote(true)
                        try {
                          console.log(`📝 [FocusMode] Saving note to task ${focusItem.id}: ${focusItem.title}`)
                          // Save note to the task
                          const response = await fetch(`/api/progress/item/task/${focusItem.id}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                              notes: focusModeNote.trim(),
                              progress_value: focusItem.progress || 0
                            })
                          })
                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}))
                            console.error('Failed to save note:', response.status, errorData)
                            alert(`Failed to save note: ${errorData.error || 'Unknown error'}`)
                            return
                          }
                          console.log(`✅ [FocusMode] Note saved successfully`)
                          setFocusModeNote('')
                          setFocusModeNoteExpanded(false)
                          // Refresh activity log
                          if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('activity-log-refresh'))
                          }
                        } catch (error) {
                          console.error('Error saving note:', error)
                        } finally {
                          setSavingFocusNote(false)
                        }
                      }}
                      disabled={!focusModeNote.trim() || savingFocusNote}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                        !focusModeNote.trim() || savingFocusNote
                          ? 'bg-soft text-muted cursor-not-allowed'
                          : 'bg-brand text-on-brand hover:bg-brand-hover'
                      }`}
                    >
                      {savingFocusNote ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-3 h-3 inline mr-1" />
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="flex items-center gap-6 mt-8">
          <button
            onClick={prevFocusItem}
            disabled={currentFocusIndex === 0}
            className="p-4 rounded-full bg-surface/10 hover:bg-surface/20 text-on-brand disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="w-8 h-8 rotate-180" />
          </button>
          <button
            onClick={nextFocusItem}
            disabled={currentFocusIndex === pendingItems.length - 1}
            className="p-4 rounded-full bg-surface/10 hover:bg-surface/20 text-on-brand disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>
        
      </div>
    )
  }
  
  // Loading state. Defect fix 2026-08-29: this used to short-circuit to a bare
  // centred spinner, which is why TodayOneClearThing's SkeletonTodayFocus (and
  // the `loading` prop feeding it) had never once rendered. The flag is passed
  // down instead, so the page shows the shape of the day it is loading.

  // ONE Auto-Mom surface stack. It once had to be shared by two page
  // returns, which drifted twice in one day (curtain, then the release-offer
  // card, invisible in the orchestrator view while a real surfaced offer
  // sat pending — owner report 2026-08-24). The second return is gone as of
  // 2026-08-29, but the rule stands: add every Auto-Mom surface HERE.
  // ── The split (owner ask 2026-08-30) ────────────────────────────────
  // This used to be ONE `autoMomStack` blob rendered above the hero card,
  // and that single fact is most of why the page was unfollowable: five
  // surfaces from four different backends, every one of them phrased as if
  // it were the thing to do next, all sitting above the page's actual
  // recommendation. On an ordinary evening the hero card landed TENTH.
  //
  // The two halves are doctrinally different and now render in different
  // zones, so the difference is finally visible:
  //   commitments — FIDELITY. Things the user put on a clock themselves.
  //                 Never capped, never filtered, never deferred. Zone 1.
  //   asks        — RESTRAINT. Everything yanamas proposes. Optional, and
  //                 the zone says so out loud. Zone 4, below the user's
  //                 own day.
  // Add every new Auto-Mom surface to `asks`, not to `commitments`.
  // F5 Reminder Contracts — what the user asked to be held to — are drawn by
  // the page itself since 2026-09-12, in the two lanes the mockup uses (the
  // due bar under the band, the Reminders card for what has no clock), so
  // nothing is handed down here any more.

  // Everything this page asks of you, handed to the ONE zone that orders them.
  //
  // Until 2026-09-08 these five rendered here as a stack, each with its own
  // "Ori ..." heading, above a sixth zone that had four more. Owner: *"all seem
  // to be different and they are confusing me ... What is my direction to
  // follow? I want 1 guidance."* They now go in as `extraAsks` and are ranked
  // against the zone's own asks by `ORI_RANK` — work you could do now, then a
  // decision that BLOCKS scheduling, then structure, then the rest.
  //
  // The `kind` is the ONLY thing decided here; the ordering lives in one place
  // (`utils/oriAsks.ts`) so a new ask cannot quietly jump the queue.
  // The five asks' data, owned HERE (owner 2026-09-08: *"lift those five
  // fetches up so the count is right"*).
  //
  // Each card used to fetch for itself and return null when it had nothing,
  // which made the zone's "N to answer" a guess: a collapsed zone never mounts
  // its children, so nothing had fetched, and the header counted asks that
  // turned out to be empty the moment you opened it. Two of the five were
  // already known here — the prepared-day snapshot and the release offer that
  // arrives on today-view — so these are the other three.
  //
  // `count === null` means NOT KNOWN YET, which is not the same as zero: an
  // ask that has not answered is left undecided rather than hidden.
  // ─── Refined View Adapter ───
  // Maps current component state to OneClearThingProps
  if (useRefinedView) {
    const toItemLike = (t: FocusTask): TodayItemLike => ({
      id: t.task_id,
      title: t.title,
      description: t.description,
      priority: (t.priority || 'medium') as TodayItemLike['priority'],
      status: (t.status || 'pending') as TodayItemLike['status'],
      estimated_duration: t.estimated_duration,
      parent_goal_title: t.parent_goal_title,
      is_recurring: t.is_recurring,
      user_pinned_for_today: t.user_pinned_for_today,
    })

    const legacyToItemLike = (t: TodayItem): TodayItemLike => ({
      id: t.id,
      title: t.title,
      description: t.description,
      priority: t.priority,
      status: t.status,
      estimated_duration: t.estimated_duration,
      start_time: t.start_time,
      end_time: t.end_time,
      parent_goal_title: t.parent_title,
      is_recurring: t.is_recurring,
    })

    const toFocusTaskItem = (t: FocusTask): FocusTaskItem => ({
      task_id: t.task_id,
      title: t.title,
      description: t.description,
      status: t.status,
      priority: t.priority,
      estimated_duration: t.estimated_duration,
      is_recurring: t.is_recurring,
      is_scheduled_today: t.is_scheduled_today,
      scheduled_time_slot: t.scheduled_time_slot,
      action_items_count: t.action_items?.length ?? 0,
      action_items_completed: t.action_items?.filter((a: any) => a.status === 'completed').length ?? 0,
      parent_goal_title: t.parent_goal_title,
      progress: t.progress,
      incremental: t.incremental,
      sessions_done: t.sessions_done,
      sessions_target: t.sessions_target,
      user_pinned_for_today: t.user_pinned_for_today,
      deadline_type: t.deadline_type,
      deadline_consequence: t.deadline_consequence,
      reasoning_tags: getReasoningTags(t.task_id),
      // Due after today → when-chip reads "For <date>". Plain YYYY-MM-DD
      // string comparison — no Date()/TZ math (rig VMs are time-shuttled).
      future_date_label:
        t.due_date && serverDateString && t.due_date.slice(0, 10) > serverDateString
          ? t.due_date.slice(0, 10)
          : undefined,
    })

    const toHabitOccurrenceItem = (occ: HabitOccurrence): HabitOccurrenceItem => ({
      id: occ.id,
      habit_id: occ.habit_id,
      title: occ.title,
      status: occ.status,
      time_slot: occ.time_slot,
      duration_minutes: occ.duration_minutes,
      minimum_viable_repetition: occ.minimum_viable_repetition,
      cue_or_anchor: occ.cue_or_anchor,
      master_id: occ.master_id,
      date: occ.date,
    })

    const habits = todayViewData?.daily_habits || todayViewData?.recurring_tasks || []
    const rawFocusTasks = todayViewData?.focus_tasks || []
    const completedTasks = todayViewData?.completed_tasks || []
    const inProgressBacklog = momentumState.in_progress_backlog || []
    const scheduledEvents = todayViewData?.scheduled_events || []
    const rawHabitOccurrences = todayViewData?.habit_occurrences || []

    /** Four named answers, replacing the 0-4 slider index (2026-09-11). The
     *  store was empty, so the names ARE the contract now; the server still
     *  folds the old five-point labels onto them (`mood_service.normalise`). */
    /** Re-read the check-in so the suggestion line reflects the new answer. */
    const refreshCheckIn = async () => {
      try {
        const r = await fetch(`/api/orchestrator/daily-checkin?date=${serverDateString}`,
                              { credentials: 'include' })
        if (!r.ok) return
        const b = await r.json()
        setDayMood(b.mood ?? null)
        setMoodSuggestion(b.suggested_because ?? null)
      } catch { /* the chips still show what you picked */ }
    }

    const submitMood = async (mood: 'low' | 'okay' | 'good' | 'great') => {
      setDayMood(mood)
      try {
        const response = await fetch('/api/orchestrator/daily-checkin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mood, date: serverDateString }),
        })
        if (response.ok) {
          setDailyCheckInCompleted(true)
          void refreshCheckIn()
          fetch('/api/orchestrator/engagement-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ event_type: 'checkin_submitted', context: mood }),
          }).catch(console.warn)
        }
      } catch (error) {
        console.error('Error submitting check-in:', error)
      }
    }

    const carryoverCopy = incompleteTasks.length > 0
      ? getCarryoverUX({ personality, userState, count: incompleteTasks.length })
      : null

    // FIX 2026-08-29: the backend composes recovery copy knowing severity,
    // absence_days and readiness, and runs it through the tone guard. The
    // refined page threw it away and used local strings; prefer it, fall back
    // to the local copy. Only when recovery is actually needed — otherwise
    // decision.copy holds the all-clear message, not a carryover banner.
    const backendBanner = recoveryDecision?.recovery_needed
      ? (recoveryDecision.copy?.banner ?? recoveryDecision.copy)
      : undefined

    const handleCarryoverTaskAction = async (taskId: number, actionId: string, actionData: Record<string, any> = {}) => {
      setCarryoverProcessingTaskId(String(taskId))
      try {
        const response = await fetch(`/api/orchestrator/incomplete-tasks/${taskId}/action`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action: actionId, data: actionData })
        })

        if (response.ok) {
          fetch('/api/orchestrator/engagement-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              event_type: 'carryover_task_action',
              context: actionId,
              data: { task_id: taskId, reason: actionData.reason ?? null }
            })
          }).catch(console.warn)

          setIncompleteTasks(prev => {
            const remaining = prev.filter(t => t.id !== taskId)
            if (remaining.length === 0) {
              setShowIncompleteModal(false)
              updateRecoveryDecisionStatus('resolved')
              fetchTodayViewFromOrchestrator(true)
            }
            return remaining
          })
        }
      } catch (error) {
        console.error('Error handling task action:', error)
      } finally {
        setCarryoverProcessingTaskId(null)
      }
    }

    const refinedProps: OneClearThingProps = {
      serverDateLabel: serverDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
      // FIX 2026-08-29: the masthead Refresh used to refetch the LEGACY list,
      // which is not what the header renders — the counters never moved.
      // Match the auto-refresh path instead.
      onRefresh: () => {
        if (timeRange === 'today') {
          void fetchTodayViewFromOrchestrator(true)
        } else {
          void fetchTodayItems(true)
        }
      },
      refreshing,

      // Defect fix 2026-08-29: TodayOneClearThing has always carried a loading
      // skeleton and an error-with-retry screen, but neither prop was ever
      // passed — so a broken backend rendered the calm "Your day is wide open"
      // empty state. Wired now: `loading` drives the skeleton, and the error
      // takes over the page only when there is genuinely nothing loaded to
      // show — so a failed 5-minute auto-refresh can't wipe a day that is
      // already on screen (it stays, and the next good read clears the flag).
      loading,
      error: (!todayViewData && items.length === 0) ? loadError : null,
      onRetry: () => {
        if (timeRange === 'today') {
          void fetchTodayViewFromOrchestrator(false)
        } else {
          void fetchTodayItems(false)
        }
      },

      progress,
      dailyGoalLabel: String(momentumState.recovery_mode === 'low_momentum' ? momentumState.effective_limit : dailyFocusLimit),
      completedTodayCount,
      isAboveAndBeyond,
      beyondCount,

      // Recovery / momentum
      recoveryMode: momentumState.recovery_mode,
      recoveryMessage: momentumState.recovery_message,
      effectiveLimit: momentumState.effective_limit,
      dailyFocusLimit,

      // Sections data
      focusTasks: rawFocusTasks.filter(t => t.status !== 'completed').map(toFocusTaskItem),
      dailyHabits: habits.map(toItemLike),
      habitOccurrences: rawHabitOccurrences.map(toHabitOccurrenceItem),
      inProgressBacklog: inProgressBacklog.map(toItemLike),
      // The read model rides ALL THE WAY through to the timeline. This mapper
      // used to be an explicit key literal that predated the Phase 2 fields, so
      // commitment / commitment_source / block_source / plan_role /
      // last_reconciled_at / replacement_count were stamped by the server and
      // then dropped one hop before the surface that renders them: no
      // Flexible/Confirmed chip, no Confirm verb, no "Why this?", and a
      // scheduled block reading as UNSCHEDULED. Spreading the row first means
      // a key the endpoint adds tomorrow arrives instead of being deleted here;
      // the overrides below only normalize types.
      timelineEvents: scheduledEvents.map(ev => ({
        ...ev,
        id: String(ev.id),
        item_id: ev.item_id ?? null,
        item_type: ev.item_type ?? null,
        title: ev.title,
        start_time: ev.start_time,
        end_time: ev.end_time,
        source: ev.source || 'horton',
        color: (ev as { color?: string }).color || 'blue',
        is_all_day: ev.is_all_day,
        calendar_name: ev.calendar_name,
        completed: Boolean(ev.completed),
        // Explicit, not spread-implicit: `TimelineEvent` requires each of these
        // (nullable), so dropping one is a compile error rather than a silently
        // dead feature. `?? null` keeps "the server said nothing" honest.
        commitment: ev.commitment ?? null,
        commitment_source: ev.commitment_source ?? null,
        block_source: ev.block_source ?? null,
        plan_role: ev.plan_role ?? null,
        last_reconciled_at: ev.last_reconciled_at ?? null,
        replacement_count: ev.replacement_count ?? null,
      })),
      completedItems: completedTasks.map(toItemLike),
      completedYesterday: completedYesterday.map(legacyToItemLike),
      hardDeadlineWarnings: todayViewData?.hard_deadline_warnings || [],
      backlogSummary: momentumState.backlog_summary,

      // Carryover — gated on the policy count, not the raw task list.
      carryoverItems: effectiveCarryoverCount > 0
        ? incompleteTasks.map((t: any) => ({ id: t.id, title: t.title }))
        : [],
      carryoverTitle: backendBanner?.title ?? carryoverCopy?.banner.title,
      carryoverBody: backendBanner?.body ?? carryoverCopy?.banner.body,
      carryoverPrimaryLabel: (backendBanner as { primaryLabel?: string } | undefined)?.primaryLabel
        ?? carryoverCopy?.banner.primaryLabel,
      onReviewCarryover: effectiveCarryoverCount > 0
        ? () => {
          // FIX 2026-08-29: the legacy banner emitted this; the refined one
          // emitted nothing, so carryover engagement went dark.
          fetch('/api/orchestrator/engagement-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              event_type: 'carryover_banner_cta_clicked',
              context: 'with_you',
              data: { count: effectiveCarryoverCount },
            }),
          }).catch(console.warn)
          setShowIncompleteModal(true)
        }
        : undefined,

      // Coach
      alerts: alerts.filter(a => a.action).slice(0, 3).map(a => ({
        id: a.id,
        title: a.title,
        message: a.message,
        severity: a.severity,
        action: a.action ? { label: a.action.label, url: a.action.url } : undefined,
      })),
      habitAdjustmentCheckin: habitAdjustment.checkin,
      habitAdjustmentCheckins: habitAdjustment.checkins,
      habitAdjustmentRevisitDue: habitAdjustment.revisitDue,
      onHabitAdjustmentShown: habitAdjustment.markShown,
      onHabitAdjustmentRespond: habitAdjustment.respond,
      onHabitAdjustmentDiagnose: habitAdjustment.diagnose,
      onHabitAdjustmentChooseAction: habitAdjustment.chooseAction,
      onHabitAdjustmentConfirm: habitAdjustment.confirm,
      onHabitAdjustmentPause: habitAdjustment.pause,
      onHabitAdjustmentRestart: habitAdjustment.restart,
      onHabitAdjustmentArchive: habitAdjustment.archive,
      onHabitAdjustmentRefresh: habitAdjustment.refetch,

      // AutoMom goal progress review
      goalReviewPrompts: goalReview.prompts,
      onGoalReviewShown: goalReview.markShown,
      onGoalReviewRespond: goalReview.respond,
      onGoalReviewDone: (result) => {
        if (result.next_step === 'breakdown' && result.goal_id) {
          navigate(`/goals/${result.goal_id}?action=breakdown`)
        }
        goalReview.refetch()
      },

      skillCheckpoints: skillCheckpoints.prompts,
      onSkillCheckpointShown: skillCheckpoints.markShown,
      onSkillCheckpointRespond: skillCheckpoints.respond,
      onSkillCheckpointDone: () => {
        // Refresh tasks too: confirming a level can unblock dependent tasks.
        skillCheckpoints.refetch()
      },

      onAlertAction: (_alertId, url) => {
        if (url) navigate(url)
      },

      // Mood slider
      dailyCheckInCompleted,
      dayMood,
      moodSuggestion,
      onPickMood: submitMood,
      moodSliderValue,
      onMoodSliderChange: (val: number) => setMoodSliderValue(val),
      onMoodSliderSubmit: () => void submitMood(dayMood ?? 'okay'),

      // Prepared-day counters refetch whenever today's plan moves.
      planVersion,

      // Actions
      // Always wired; the page disables the switch when nothing is pending
      // instead of hiding it (owner, 2026-09-11: "the 'Focus' mode … is no
      // longer present" — it had hidden itself on a finished day).
      onOpenFocusMode: () => { setCurrentFocusIndex(0); setFocusMode(true) },
      focusModeAvailable: pendingItems.length > 0,
      onOpenAiAssist: () => navigate('/review-schedule', { state: { items } }),
      onStartTask: (taskId) => {
        const parsed = parseInt(taskId, 10)
        if (!isNaN(parsed)) {
          startItem({ id: parsed, title: '', type: 'task', priority: 'medium', status: 'pending' })
        }
      },
      onCompleteTask: (taskId) => {
        const parsed = parseInt(taskId, 10)
        if (!isNaN(parsed)) {
          toggleItemComplete({ id: parsed, title: '', type: 'task', priority: 'medium', status: 'pending' })
        }
      },
      onOpenTask: (taskId) => {
        loadTaskDetail(taskId)
      },
      onPinTask: (taskId, pinned) => {
        handlePinTask(taskId, pinned)
      },
      onLogProgress: (taskId) => {
        handleLogProgress(taskId)
      },
      onCompleteHabit: (taskId) => {
        const id = typeof taskId === 'number' ? taskId : parseInt(String(taskId), 10)
        if (!isNaN(id)) {
          toggleItemComplete({ id, title: '', type: 'task', priority: 'medium', status: 'pending' })
        }
      },
      onCompleteHabitOccurrence: async (occ) => {
        try {
          const body: any = occ.master_id
            ? { master_id: occ.master_id, date: occ.date || new Date().toISOString().slice(0, 10) }
            : { occurrence_id: occ.id }
          await fetch('/api/schedule/habit-complete', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
          setTodayViewData(prev => {
            if (!prev) return prev
            return {
              ...prev,
              habit_occurrences: (prev.habit_occurrences || []).map(h =>
                h.id === occ.id ? { ...h, status: 'completed' } : h
              ),
            }
          })
          // "Completing this also helps X" — held/saved thoughts this habit
          // moves forward (fire-and-forget, silent when none). Same doctrine
          // gate as the task path: the celebration lands alone (2026-08-29).
          if (occ.title && !willCelebrateAfter({ occurrenceId: occ.id })) {
            void connectionForCompletedItem({ type: 'habit', title: occ.title }).then((cn) => {
              if (!cn) return
              toast.toast({
                variant: 'info',
                title: `Completing this also helps "${cn.thread.title}"`,
                description: cn.why,
                action: { label: 'See it', onClick: () => navigate('/horton-inbox') },
              })
            })
          }
        } catch (err) {
          console.error('Failed to complete habit occurrence', err)
        }
      },
      // A FAILED read must not look like an empty habit. Defect found
      // 2026-08-30 by tests/ui-behavior/03-today-response: this used to
      // swallow every error and return null, and HabitChip's own guard —
      // `!loading && !fetchError && actionItems.length === 0 && !detail`
      // (TodayFocusRefined.tsx:484) — then rendered "No steps defined for
      // this habit" on a 500. Because the callback could never throw, the
      // `catch { setFetchError(true) }` in handleExpand was unreachable and
      // the "Couldn't load details / Retry" branch beside it was dead code.
      // THROWING is the signal the caller is already written for: null now
      // means "read fine, nothing there", an exception means "could not
      // read", and the two stop being the same thing on screen.
      onFetchHabitDetail: async (id) => {
        const numId = typeof id === 'string' ? parseInt(id, 10) : id
        const res = await fetch(`/api/habit-execution/v1/occurrences/${numId}`, { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          return data.occurrence || null
        }
        // Fallback: try definition endpoint for daily habits (non-occurrence)
        const defRes = await fetch(`/api/habit-execution/v1/definitions/${numId}`, { credentials: 'include' })
        if (defRes.ok) {
          const defData = await defRes.json()
          return defData.definition || null
        }
        throw new Error(
          `habit detail unavailable (occurrence ${res.status}, definition ${defRes.status})`,
        )
      },
      onToggleHabitActionItem: async (occurrenceId, actionItemId, done) => {
        try {
          const numOccId = typeof occurrenceId === 'string' ? parseInt(occurrenceId, 10) : occurrenceId
          await fetch(`/api/habit-execution/v1/occurrences/${numOccId}/action-items/${actionItemId}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: done ? 'completed' : 'pending' }),
          })
        } catch (err) {
          console.error('Failed to toggle habit action item', err)
        }
      },
      onEscapeBacklog: (taskId) => {
        // Navigate to reschedule or handle escape
        loadTaskDetail(taskId)
      },
      onEscapeBlocked: (taskId) => {
        loadTaskDetail(taskId)
      },
      onDeadlineAction: (taskId) => {
        loadTaskDetail(taskId)
      },
      pinningTaskId: pinningTask,
      onNavigateToCreateGoal: () => navigate('/goals/create'),
      onNavigateToCreateTask: () => navigate('/tasks/create'),
      onNavigateToSchedule: () => navigate('/review-schedule'),
    }

    return (
      <div className="min-h-screen bg-default">
        {/* Theme switch (both modes) + the yanamas mission flyleaf, which
            renders only in diary mode and only until it's tucked away. */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 space-y-3">
          <YanamasCustomizer />
          <YanamasFlyleaf />
        </div>
        <TodayOneClearThing {...refinedProps} extraAsks={extraAsks}
          // The FILTERED count: the timeline points at what the curtain will
          // actually draw, not at options already on today's plate.
          preparedLiveCount={recoveryTriagePending ? 0 : (curtainOffers ?? 0)}
          // Rule 0: the prepared day's top live pick decides what "Start here"
          // leads with, instead of offering a rival answer one zone below.
          // `liveItemIds` is in rank order, so [0] is Auto-Mom's own choice.
          stretch={stretch.data}
          preparedTopItemId={preparedTopItemId}
          onTakeOn={async (taskId) => {
            // Pin AND add to today's plan — /pin-task alone never reached the
            // rendered FocusPlan, so a pinned stretch item would have stayed
            // in Stretch and out of Do until the next refill.
            const ok = await takeOn(taskId)
            if (ok) { await fetchTodayViewFromOrchestrator(true); void stretch.refresh() }
            return ok
          }}
          // The energy rung feeds both the plan and Stretch's slot count.
          onCapacityChanged={async () => {
            await fetchTodayViewFromOrchestrator(true)
            void stretch.refresh()
          }}
          // Quick add from the band (2026-09-12 mockup): the list pages' own
          // create, then the view refetches so admission's placement shows.
          onQuickAdd={async (title) => {
            let created: { id?: number | string } | null = null
            try {
              created = await new TaskService().createTask({ title, priority: 'medium' } as CreateTaskRequest)
            } catch (err) {
              toast.error(TODAY_COPY.quickAddFailed, err instanceof Error ? err.message : undefined)
              throw err
            }
            await fetchTodayViewFromOrchestrator(true)
            const id = Number(created?.id)
            toast.toast({
              variant: 'success', title: TODAY_COPY.quickAddSaved, description: title,
              action: Number.isFinite(id) ? { label: 'Open', onClick: () => navigate(`/tasks/${id}`) } : undefined,
            })
          }}
          // The Focus card's "Not today": tomorrow, via the carryover route
          // (which also drops the task from today's focus). A pin is
          // date-scoped and today's pin would pull the task straight back,
          // so it is lifted first.
          onNotToday={async (taskId) => {
            const id = parseInt(taskId, 10)
            if (isNaN(id)) return
            const d = new Date()
            d.setDate(d.getDate() + 1)
            const tomorrow = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            const pinned = rawFocusTasks.some(t => String(t.task_id) === taskId && t.user_pinned_for_today)
            if (pinned) await handlePinTask(taskId, false)
            const res = await fetch(`/api/orchestrator/incomplete-tasks/${id}/action`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ action: 'reschedule', data: { new_date: tomorrow } }),
            })
            if (!res.ok) {
              toast.error(TODAY_COPY.verbFailed)
              return
            }
            await fetchTodayViewFromOrchestrator(true)
            toast.toast({ variant: 'success', title: TODAY_COPY.notTodayDone })
          }} />
        {/* Task Detail Modal */}
        {selectedTask && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-primary">{selectedTask.title}</h3>
                <button
                  type="button"
                  aria-label="Close task details"
                  onClick={() => setSelectedTask(null)}
                  className="text-muted hover:text-secondary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {selectedTask.description && (
                <p className="text-sm text-secondary mb-3">{selectedTask.description}</p>
              )}
              <div className="flex gap-2 mt-4">
                <Button onClick={() => navigate(`/tasks/${selectedTask.id}`)} className="flex-1">
                  View Full Details
                </Button>
                <Button onClick={() => setSelectedTask(null)} variant="outline">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Carryover Modal */}
        {showIncompleteModal && incompleteTasks.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 p-5 border-b border-soft">
                <div>
                  <h3 className="text-lg font-semibold text-primary">{carryoverCopy?.modal.title || 'Quick reset'}</h3>
                  <p className="mt-1 text-sm text-secondary">
                    {(recoveryDecision?.absence_days ?? 0) >= 2
                      ? "You've been away — what still matters right now? Keep what's alive, shelve the rest; every answer sticks."
                      : 'Choose where each open task belongs now. Each choice removes that task from this review; when all tasks have a decision, Today refreshes.'}
                  </p>
                </div>
                {/* Named on purpose: both dialog dismissals were bare icon
                    buttons with no accessible name, so a screen reader
                    announced only "button" on the one control that closes a
                    modal covering the whole page (found 2026-08-29 by
                    tests/ui-behavior). */}
                <button
                  type="button"
                  aria-label="Close this review for now"
                  onClick={() => closeCarryoverModal('snoozed')}
                  className="text-muted hover:text-secondary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 px-5 py-3 border-b border-soft bg-surface-alt">
                <div className="rounded-lg border border-soft bg-surface px-2 py-2">
                  <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Keep Today</p>
                  <p className="mt-0.5 text-xs text-secondary">Stays on Today, held as a commitment.</p>
                </div>
                <div className="rounded-lg border border-soft bg-surface px-2 py-2">
                  <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Backlog</p>
                  <p className="mt-0.5 text-xs text-secondary">Steps back until you call it — or it comes due.</p>
                </div>
                <div className="rounded-lg border border-soft bg-surface px-2 py-2">
                  <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Done</p>
                  <p className="mt-0.5 text-xs text-secondary">Marks complete.</p>
                </div>
              </div>

              <div className="space-y-3 p-5">
                {incompleteTasks.map((task: any) => {
                  const isProcessing = carryoverProcessingTaskId === String(task.id) || carryoverProcessingTaskId === 'bulk'
                  return (
                    <div key={task.id} className={`rounded-xl border border-soft p-3 ${isProcessing ? 'opacity-60 pointer-events-none' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-primary">{task.title}</p>
                          {task.missed_date && (
                            <p className="mt-0.5 text-xs text-secondary">Open since {task.missed_date}</p>
                          )}
                          {task.source === 'committed_lapsing' && (
                            <p className="mt-0.5 text-xs text-brand">Commitment about to lapse — still on?</p>
                          )}
                          {(() => {
                            // F7 CT2 guard: shelving a NEAR-DUE task is fine
                            // but never silent — it surfaces at its deadline
                            // regardless of Backlog.
                            //
                            // Bounded BELOW as well (2026-08-30): the guard
                            // only tested `dl > soon`, and every row in this
                            // review is overdue by definition, so it fired on
                            // all of them and promised that a date already
                            // gone would "surface then". It cannot — Backlog
                            // clears due_date outright. Compared as plain
                            // YYYY-MM-DD against the SERVER's date, because
                            // `new Date()` here was the BROWSER's clock: wrong
                            // for a user in another timezone and wrong on a
                            // time-shuttled rig.
                            const due = task.due_date ? String(task.due_date).slice(0, 10) : ''
                            if (!/^\d{4}-\d{2}-\d{2}$/.test(due) || !serverDateString) return null
                            if (due < serverDateString) return null
                            const soonDate = new Date(`${serverDateString}T00:00:00Z`)
                            soonDate.setUTCDate(soonDate.getUTCDate() + 2)
                            if (due > soonDate.toISOString().slice(0, 10)) return null
                            return (
                              <p className="mt-0.5 text-xs text-secondary italic">
                                due {String(task.due_date).slice(0, 10)} — surfaces then even from Backlog
                              </p>
                            )
                          })()}
                        </div>
                        {isProcessing && <Loader2 className="w-4 h-4 animate-spin text-brand flex-shrink-0 mt-0.5" />}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <button
                          onClick={() => void handleCarryoverTaskAction(Number(task.id), 'keep_today')}
                          disabled={isProcessing}
                          className="inline-flex items-center justify-center gap-1.5 min-h-touch px-2 py-1.5 text-xs font-medium rounded-lg bg-brand-subtle hover:bg-brand-subtle text-brand border border-brand transition-colors"
                        >
                          <Target className="w-3.5 h-3.5" />
                          Keep today
                        </button>
                        {/* Contract §5 (2026-09-04): "Not today" asks WHY before it
                            acts — the reason is the one input a better next action
                            needs, and it goes to the day's outcome, not to a note. */}
                        <button
                          onClick={() => setCarryoverReasonFor(carryoverReasonFor === Number(task.id) ? null : Number(task.id))}
                          disabled={isProcessing}
                          aria-expanded={carryoverReasonFor === Number(task.id)}
                          className="inline-flex items-center justify-center gap-1.5 min-h-touch px-2 py-1.5 text-xs font-medium rounded-lg bg-surface-alt hover:bg-surface-alt text-secondary border border-soft transition-colors"
                          data-testid="carryover-not-today"
                        >
                          <ListTodo className="w-3.5 h-3.5" />
                          Not today
                        </button>
                        <button
                          onClick={() => void handleCarryoverTaskAction(Number(task.id), 'offline_done', { reason: 'done_elsewhere' })}
                          disabled={isProcessing}
                          className="inline-flex items-center justify-center gap-1.5 min-h-touch px-2 py-1.5 text-xs font-medium rounded-lg bg-ok/20 hover:bg-ok/30 text-ok border border-ok transition-colors"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Done
                        </button>
                      </div>
                      {carryoverReasonFor === Number(task.id) && (
                        <div className="mt-2" role="group" aria-label="Why not today?">
                          <p className="text-xs text-muted mb-1.5">What got in the way? One tap is enough.</p>
                          <div className="flex flex-wrap gap-1.5">
                            {([
                              { reason: 'no_time', label: 'No time', action: 'backlog' },
                              { reason: 'not_ready', label: 'Wasn\'t ready', action: 'backlog' },
                              { reason: 'blocked', label: 'Blocked', action: 'mark_blocked' },
                              { reason: 'lost_interest', label: 'Lost interest', action: 'backlog' },
                              { reason: 'done_elsewhere', label: 'Did it elsewhere', action: 'offline_done' },
                            ] as const).map((r) => (
                              <button
                                key={r.reason}
                                onClick={() => { setCarryoverReasonFor(null); void handleCarryoverTaskAction(Number(task.id), r.action, { reason: r.reason }) }}
                                disabled={isProcessing}
                                className="min-h-touch px-3 py-1.5 rounded-full border border-strong bg-surface text-xs font-medium text-secondary hover:border-brand transition-colors"
                                data-testid={`carryover-reason-${r.reason}`}
                              >
                                {r.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <div className="flex flex-col gap-3 p-5 border-t border-soft bg-surface-alt rounded-b-2xl">
                {/* F7 §6.2: one-tap sort for the whole list; per-task buttons refine. */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      setCarryoverProcessingTaskId('bulk')
                      try {
                        // CT5: "every answer sticks" — only drop rows whose
                        // POST actually succeeded; failures stay listed.
                        const failed: any[] = []
                        for (const t of incompleteTasks) {
                          const res = await fetch(`/api/orchestrator/incomplete-tasks/${t.id}/action`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ action: 'keep_today', data: {} })
                          }).catch(() => null)
                          if (!res || !res.ok) failed.push(t)
                        }
                        setIncompleteTasks(failed)
                        if (failed.length === 0) {
                          setShowIncompleteModal(false)
                          updateRecoveryDecisionStatus('resolved')
                        }
                        fetchTodayViewFromOrchestrator(true)
                      } catch (err) { console.error('bulk keep failed', err) }
                      finally { setCarryoverProcessingTaskId(null) }
                    }}
                    disabled={carryoverProcessingTaskId === 'bulk'}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-brand-subtle text-brand border border-brand transition-colors disabled:opacity-50"
                  >
                    <Target className="w-3.5 h-3.5" />
                    Keep all remaining
                  </button>
                  <button
                    onClick={async () => {
                      setCarryoverProcessingTaskId('bulk')
                      try {
                        const failed: any[] = []
                        for (const t of incompleteTasks) {
                          const res = await fetch(`/api/orchestrator/incomplete-tasks/${t.id}/action`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ action: 'backlog', data: {} })
                          }).catch(() => null)
                          if (!res || !res.ok) failed.push(t)
                        }
                        setIncompleteTasks(failed)
                        if (failed.length === 0) {
                          setShowIncompleteModal(false)
                          updateRecoveryDecisionStatus('resolved')
                        }
                        fetchTodayViewFromOrchestrator(true)
                      } catch (err) { console.error('bulk backlog failed', err) }
                      finally { setCarryoverProcessingTaskId(null) }
                    }}
                    disabled={carryoverProcessingTaskId === 'bulk'}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-surface text-secondary border border-soft transition-colors disabled:opacity-50"
                  >
                    <ListTodo className="w-3.5 h-3.5" />
                    Backlog all remaining
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => closeCarryoverModal('snoozed')}
                  className="px-4 py-2 text-sm text-secondary hover:text-primary transition-colors"
                >
                  Remind me later
                </button>
                <Button
                  onClick={() => {
                    closeCarryoverModal('dismissed')
                    setCarryoverDismissed(true)
                    setIncompleteTasks([])
                  }}
                  variant="outline"
                  size="sm"
                >
                  Dismiss
                </Button>
                </div>
              </div>
                  </div>
          </div>
        )}
      </div>
    )
  }

  // Unreachable since 2026-08-29. `useRefinedView` is initialised true and
  // has no setter left, so the branch above always returns. The ~2,650-line
  // legacy Today render that used to live here was deleted with it, including
  // its dead "New UI" button (it only ever set useRefinedView to the value it
  // already had) and a "Missed <date>" carryover label that broke the
  // no-shaming rule. Everything live now renders through TodayOneClearThing.
  return null
}
